// Simplified Lumi Chat Component V2
// Single API call architecture - no separate intent classification
import { useState, useRef, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePlotsStore } from '@/store/plots';
import { useProjectsStore } from '@/store/projects';
import { useAuthStore } from '@/store/auth';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams } from 'wouter';
import { apiRequest, authorizedFetch, scopedKey } from '@/lib/queryClient';
// import { handleInsightPayload } from '@/lib/insights/insights-utils';
import { InsightsPanel } from '@/components/insights/InsightsPanel';
import { useQueryClient } from '@tanstack/react-query';
import { mapToChip, advanceMonotonic, type ChipState } from '@/lib/progress-map';
import { safeNormalizeInsightsPayload } from '@/lib/insights/insights-utils';
import { logEvt } from '@/dev/hydration-debug';
// import { extractTakehome } from '@/lib/takehome';
import { Plus, Images, Grid3x3, Loader2, ChevronDown, ChevronUp, Copy, Check, Info } from 'lucide-react';
import FileUpload from '@/components/upload/file-upload';
// import { useWebSocket } from '@/hooks/use-websocket';
import { socket as socketSingleton, subscribeProject, unsubscribeProject } from '@/ws/socket';
import { LumiAvatarInteractive } from '@/components/icons/lumi-avatar-interactive';
import { useLatestLumiAvatar } from '@/hooks/use-latest-lumi-avatar';
import { ArrowUpIcon } from '@/components/icons/arrow-up';
// Option A components kept for reference; shelves is the active layout
// import ChartCategoryTabs from '@/components/builder/ChartCategoryTabs';
// import ChartTypeGrid from '@/components/builder/ChartTypeGrid';
import ChartTypeShelves from '@/components/builder/ChartTypeShelves';
import ChartBuilderCard from '@/components/builder/ChartBuilderCard';
import { loadCharts } from '@/components/builder/charts';
import buildPrompt from '@/components/builder/utils/buildPrompt';
import ChartTypeGridDrawer from '@/components/builder/ChartTypeGridDrawer';
import { useChartRecommendations } from '@/components/builder/useChartRecommendations';
import type { BuilderBindings } from '@/components/builder/utils/buildPrompt';
import type { Fingerprint, RoleState } from '@/components/builder/recommendCharts';
import FingerprintSummaryCard from '@/components/chat/FingerprintSummaryCard';
import ModifyPlanDialog from '@/components/plan/ModifyPlanDialog';
import type { FileObject } from '@shared/schema';
import { CODEGEN_MESSAGES_PLAYFUL } from '../../../../../packages/shared/progress-messages';
// track removed from analytics; retain trackClient only
// normalizeChartName is no longer needed for selectedChartId; we send canonical IDs directly
import { trackClient } from '@/lib/obs';
import { toast, useToast } from '@/hooks/use-toast';
import { useCredits } from '@/hooks/useCredits';
import CreditGateBanner from '@/components/credits/CreditGateBanner';
import BillingModal from '@/components/BillingModal';
import { formatRelativeTime } from '@/lib/format';
import { inc as metricsInc } from '@/lib/metrics';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { StreamingText } from '@/components/chat/StreamingText';
import { useSafeTour } from '@/components/onboarding/TourManager';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  intent?: string;
  hasPlot?: boolean;
  plan?: any;
  planId?: string;
  requiresApproval?: boolean;
  approved?: boolean;
  asMarkdown?: boolean;
  forceAvatar?: boolean;
  hideAvatar?: boolean;
  selectedCard?: {
    label: 'Recommended' | 'Option 2' | 'Option 3';
    title?: string;
    recommended: boolean;
    plan?: any;   // for Recommended snapshot
    prop?: any;   // for Option 2/3 proposal snapshot
  };
  envelope?: { last_question_key?: string | null; last_question?: string | null };
}

// Minimal markdown renderer for bold subtitles (e.g., **Figure caption**)
function renderMarkdownLite(text: string): { __html: string } {
  const escapeHtml = (s: string): string => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const escaped = escapeHtml(String(text || ''));
  // Bold segments bounded by **...**; do not span newlines
  const withBold = escaped.replace(/\*\*([^\n]+?)\*\*/g, '<strong>$1</strong>');
  return { __html: withBold };
}

// Helper function to copy text to clipboard
async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}

// Copy button component for insight markdown with enhanced feedback
function InsightCopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<number | null>(null);

  const handleCopy = async () => {
    await copyToClipboard(text);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Insights copied to clipboard",
      duration: 2000,
    });
    if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current);
    // @ts-ignore setTimeout returns number in browsers
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="h-7 w-7 p-0 opacity-60 hover:opacity-100 transition-opacity flex-shrink-0 flex items-center justify-center rounded hover:bg-accent"
      aria-label="Copy to clipboard"
    >
      {copied ? (
        <Check className="h-4 w-4 text-emerald-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
      <span className="sr-only" aria-live="polite">{copied ? 'Copied to clipboard' : ''}</span>
    </button>
  );
}

// Component to render insight markdown with copy buttons
function InsightMarkdownWithCopy({ text }: { text: string }) {
  // Parse markdown into sections - works with streaming text (buttons appear as sections are detected)
  const sections = useMemo(() => {
    const parts: Array<{ title: string; content: string; fullText: string }> = [];
    const lines = text.split('\n');
    let currentSection: { title: string; startLine: number } | null = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const boldMatch = line.match(/^\*\*(.+?)\*\*$/);
      
      if (boldMatch) {
        // Save previous section if exists
        if (currentSection) {
          const content = lines.slice(currentSection.startLine + 1, i).join('\n').trim();
          const titleLine = lines[currentSection.startLine];
          parts.push({
            title: currentSection.title,
            content,
            fullText: `${titleLine}\n${content}`,
          });
        }
        // Start new section
        currentSection = { title: boldMatch[1], startLine: i };
      }
    }
    
    // Save last section (even if incomplete during streaming)
    if (currentSection) {
      const content = lines.slice(currentSection.startLine + 1).join('\n').trim();
      const titleLine = lines[currentSection.startLine];
      parts.push({
        title: currentSection.title,
        content,
        fullText: `${titleLine}\n${content}`,
      });
    }
    
    return parts;
  }, [text]);

  // If no sections found yet, use regular streaming
  if (sections.length === 0) {
    return (
      <StreamingText
        text={text}
        asMarkdown={true}
        renderMarkdown={renderMarkdownLite}
        speed={60}
        wordByWord={true}
      />
    );
  }

  // Build full text for single copy button
  const fullText = useMemo(() => {
    return sections.map(s => s.fullText).join('\n\n');
  }, [sections]);

  // Render sections with single copy button at the top
  return (
    <div className="space-y-6 relative">
      {/* Single copy button at the top right */}
      {sections.length > 0 && sections.some(s => s.content.length > 0) && (
        <div className="absolute top-0 right-0 z-10">
          <InsightCopyButton text={fullText} />
        </div>
      )}
      
      {sections.map((section, idx) => (
        <div key={idx} className="relative">
          <div className="mb-1">
            <strong className="text-sm font-bold text-muted-foreground">{section.title}</strong>
          </div>
          <div
            className="text-foreground whitespace-pre-wrap leading-snug"
            dangerouslySetInnerHTML={renderMarkdownLite(section.content)}
          />
        </div>
      ))}
    </div>
  );
}

// Build a concrete PlanSpec.v1 from a proposal (Option 2/3)
function planFromProposal(basePlan: any, proposal: any) {
  try {
    const enc = {
      x: proposal?.mappings_brief?.x ?? basePlan?.recommended?.encodings?.x ?? null,
      y: proposal?.mappings_brief?.y ?? basePlan?.recommended?.encodings?.y ?? null,
      color: proposal?.mappings_brief?.color ?? basePlan?.recommended?.encodings?.color ?? null,
      size: null,
      facet: { row: null, col: null },
      text: null,
      hover: basePlan?.recommended?.encodings?.hover ?? null,
    } as any;
    return {
      schema_version: 'PlanSpec.v1',
      plan_id: basePlan?.plan_id || `plan_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      confidence: basePlan?.confidence ?? 0.75,
      intent_summary: basePlan?.intent_summary ?? '',
      ambiguities: basePlan?.ambiguities ?? [],
      assumptions: basePlan?.assumptions ?? [],
      recommended: {
        title: proposal?.title || basePlan?.recommended?.title,
        chart_family: proposal?.chart_family || basePlan?.recommended?.chart_family,
        rationale: proposal?.brief || basePlan?.recommended?.rationale,
        encodings: enc,
        transforms: basePlan?.recommended?.transforms ?? [],
        filters: [],
        grouping: null,
        stats: basePlan?.recommended?.stats ?? [],
        display_hints: basePlan?.recommended?.display_hints ?? {},
      },
      proposals: Array.isArray(basePlan?.proposals) ? basePlan.proposals : [],
    } as any;
  } catch {
    return basePlan;
  }
}

import { useRunGuards } from '@/components/chat/useRunGuards';
import { track } from '@/analytics/mixpanel';
import { stableHashWeb as hashWeb } from '@/analytics/stableHashWeb';
const runStartMsRef = { current: new Map<string, number>() } as { current: Map<string, number> };
const lastRunIdByDraftRef = { current: new Map<string, string>() } as { current: Map<string, string> };

type InsightV2Persistent = {
  schemaVersion: 'InsightV2';
  projectId: string;
  plotId?: string | null;
  versionId?: string | null;
  generatedAt: string;
  model?: string;
  runId?: string | null;
  blocks: Array<{
    id: string;
    role: 'system' | 'assistant' | 'user';
    kind: 'summary' | 'bullet_list' | 'limitation' | 'suggestion' | 'note' | string;
    text: string;
  }>;
};

type LumiChatV2Props = {
  initialInsights?: InsightV2Persistent | null;
  isDirty?: boolean;
  onSave?: () => Promise<void>;
};

export default function LumiChatV2({ initialInsights, isDirty = false, onSave }: LumiChatV2Props = {}) {
  const { runWithGuards, execInFlightRef, isProcessing, setIsProcessing } = useRunGuards({ autoClear: true });
  // Hooks used by hydrator must be declared before any useEffect that references them
  const { currentProject, setSubscriptionAck } = useProjectsStore();
  const tourState = useSafeTour();
  const { currentPlot, setCurrentPlot, setLiveDataFingerprint, setCurrentPlotArtifacts, clearCurrentPlotArtifacts, setLiveStats, setLiveCodes, applyLiveToCurrentPlot } = usePlotsStore();
  const { projectId: routePid, plotId: routeDraftId } = useParams<{ projectId?: string; plotId?: string }>();
  const { user } = useAuthStore();
  // Debounced hydrator for reconnect/online/visibility
  const rehydrateTimerRef = useRef<number | null>(null);
  const isHydratingRef = useRef<boolean>(false);
  // Track which plots have shown a soft-fail toast to avoid duplicates
  const softFailToastShownRef = useRef<Set<string>>(new Set());
  // Dedupe for plan:card_shown telemetry per draft+plan
  const shownPlanCardsRef = useRef<Set<string>>(new Set());
  // Track last plan for transforms estimation in FE-only metrics
  const lastPlanSpecRef = useRef<any>(null);
  function quickHashHex(v: string): string {
    try {
      const salt = (window as any).__SENTRY_HASH_SALT__ || (import.meta as any)?.env?.VITE_SENTRY_HASH_SALT || 'local-dev-salt';
      const s = `${salt}:${v}`;
      let acc = 0;
      for (let i = 0; i < s.length; i++) acc = (acc * 31 + s.charCodeAt(i)) >>> 0;
      const hex = acc.toString(16).padStart(8, '0');
      return hex.repeat(8).slice(0, 64);
    } catch { return '0'.repeat(64); }
  }

  const figureHasRenderableData = (json: any): boolean => {
    try {
      if (!json || !Array.isArray(json.data)) return false;
      return json.data.some((tr: any) => {
        const hasX = Array.isArray(tr?.x) && tr.x.length > 0;
        const hasY = Array.isArray(tr?.y) && tr.y.length > 0;
        const z = tr?.z;
        const hasZ = Array.isArray(z)
          ? (Array.isArray(z[0]) ? (z as any[]).some((row: any) => Array.isArray(row) && row.length > 0) : z.length > 0)
          : false;
        // Radar/polar plots use r (radius) and theta (angle) instead of x/y
        const hasR = Array.isArray(tr?.r) && tr.r.length > 0;
        const hasTheta = Array.isArray(tr?.theta) && tr.theta.length > 0;
        // Treemap/sunburst plots use labels, parents, and values
        const hasValues = Array.isArray(tr?.values) && tr.values.length > 0;
        const hasLabels = Array.isArray(tr?.labels) && tr.labels.length > 0;
        // Sankey plots use node.label and link.value (nested structure)
        const hasSankeyNodes = Array.isArray(tr?.node?.label) && tr.node.label.length > 0 || (Array.isArray(tr?.node?.labels) && tr.node.labels.length > 0);
        const hasSankeyLinks = Array.isArray(tr?.link?.value) && tr.link.value.length > 0;
        return hasX || hasY || hasZ || (hasR && hasTheta) || (hasValues && hasLabels) || (hasSankeyNodes && hasSankeyLinks);
      });
    } catch { return false; }
  };

  const maybeToastSoftFail = (plotId: string | undefined, fig: any) => {
    try {
      const sid = String(plotId || '');
      if (!sid) return;
      if (softFailToastShownRef.current.has(sid)) return;
      const renderable = figureHasRenderableData(fig);
      if (!renderable) {
        softFailToastShownRef.current.add(sid);
        toast({
          title: 'No chart could be generated',
          description: 'We could not produce a figure from the data. No credits were used. Please try tweaking your prompt or filter. We are constantly improving Lumi\'s capacity to plot. If the issue persists, please let us know to help us improve.',
        });
      }
    } catch {}
  };
  const rehydrateCurrentPlot = async () => {
    try {
      if (activeRunIdRef.current) return; // skip while a run is in-flight
      if (isHydratingRef.current) return;
      isHydratingRef.current = true;
      const pid = String(currentProject?.id || '');
      const sid = String((usePlotsStore.getState().currentPlot as any)?.id || '');
      if (!pid || !sid) { isHydratingRef.current = false; return; }
      // Use existing loaders to hydrate latest or draft
      try {
        const [figRes, statsRes] = await Promise.all([
          authorizedFetch(`/api/ai/v2/plots/${sid}/versions/latest/plotly_figure.json`, { allow404: true }).catch(() => ({ ok: false } as any)),
          authorizedFetch(`/api/ai/v2/plots/${sid}/versions/latest/stats.json`, { allow404: true }).catch(() => ({ ok: false } as any)),
        ]);
        const latestFig = figRes && (figRes as any).ok ? await (figRes as any).json() : null;
        const latestStats = statsRes && (statsRes as any).ok ? await (statsRes as any).json() : null;
        if (latestStats) {
          try { setLiveStats(latestStats, 'version'); } catch {}
          try { setCurrentPlotArtifacts({ id: sid, projectId: pid, statsJson: latestStats, artifactSource: 'latest' } as any); } catch {}
        }
        if (latestFig) {
          try { setCurrentPlotArtifacts({ id: sid, projectId: pid, plotlyJson: latestFig, artifactSource: 'latest' } as any); } catch {}
          // If the latest figure is not renderable, show a soft-fail toast (one-time per plot)
          try { maybeToastSoftFail(sid, latestFig); } catch {}
        }
        if (!latestFig || !latestStats) {
          // Fallback to drafts
          const [df, ds] = await Promise.all([
            authorizedFetch(`/api/ai/v2/drafts/${pid}/${sid}/plotly_figure.json`).catch(() => ({ ok: false } as any)),
            authorizedFetch(`/api/ai/v2/drafts/${pid}/${sid}/stats.json`).catch(() => ({ ok: false } as any)),
          ]);
          const dFig = df && (df as any).ok ? await (df as any).json() : null;
          const dStats = ds && (ds as any).ok ? await (ds as any).json() : null;
          if (dStats) {
            try { setLiveStats(dStats, 'draft'); } catch {}
            try { setCurrentPlotArtifacts({ id: sid, projectId: pid, statsJson: dStats, artifactSource: 'draft' } as any); } catch {}
          }
          if (dFig) {
            try { setCurrentPlotArtifacts({ id: sid, projectId: pid, plotlyJson: dFig, artifactSource: 'draft' } as any); } catch {}
            // If the draft figure is not renderable, show a soft-fail toast (one-time per plot)
            try { maybeToastSoftFail(sid, dFig); } catch {}
          }
        }
      } finally {
        isHydratingRef.current = false;
      }
    } catch {}
  };

  useEffect(() => {
    const sock = socketSingleton;
    if (!sock) return;
    const debounced = () => {
      try { if (rehydrateTimerRef.current) window.clearTimeout(rehydrateTimerRef.current); } catch {}
      // 250ms debounce to avoid bursts
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rehydrateTimerRef.current = window.setTimeout(() => { void rehydrateCurrentPlot(); }, 250);
    };
    sock.on('reconnect', debounced as any);
    const onOnline = () => debounced();
    const onVis = () => { if (document.visibilityState === 'visible') debounced(); };
    try { window.addEventListener('online', onOnline); } catch {}
    try { document.addEventListener('visibilitychange', onVis); } catch {}
    return () => {
      try { sock.off('reconnect', debounced as any); } catch {}
      try { window.removeEventListener('online', onOnline); } catch {}
      try { document.removeEventListener('visibilitychange', onVis); } catch {}
    };
  }, [currentProject?.id]);
  const queryClient = useQueryClient();
  const clearedBySendRef = useRef(false);
  const lastBuilderTextRef = useRef<string>('');
  // Track whether user has manually edited/cleared input for each dataset (fingerprint)
  // When dataset changes, this resets so builder can seed prompt again
  const userTouchedInputRef = useRef<Map<string, boolean>>(new Map());
  
  // Convert InsightV2Persistent blocks to markdown content string
  const blocksToMarkdown = (blocks: InsightV2Persistent['blocks']): string => {
    if (!blocks || blocks.length === 0) return '';
    
    const sections: string[] = [];
    
    // Figure caption (summary blocks)
    const summaryBlocks = blocks.filter(b => b.kind === 'summary');
    if (summaryBlocks.length > 0) {
      const captionText = summaryBlocks.map(b => b.text.trim()).filter(Boolean).join('\n');
      sections.push(`**Figure caption**\n${captionText}`);
    }
    
    // Results (note blocks)
    const noteBlocks = blocks.filter(b => b.kind === 'note');
    if (noteBlocks.length > 0) {
      const resultsText = noteBlocks.map(b => b.text.trim()).filter(Boolean).join('\n');
      sections.push(`**Results**\n${resultsText}`);
    }
    
    // Next steps (suggestion blocks)
    const suggestionBlocks = blocks.filter(b => b.kind === 'suggestion');
    if (suggestionBlocks.length > 0) {
      const stepsText = suggestionBlocks.map(b => b.text.trim()).filter(Boolean).join('\n');
      sections.push(`**Next steps**\n${stepsText}`);
    }
    
    // Other blocks (fallback for any other kinds)
    const otherBlocks = blocks.filter(b => 
      b.kind !== 'summary' && b.kind !== 'note' && b.kind !== 'suggestion'
    );
    if (otherBlocks.length > 0) {
      const otherText = otherBlocks.map(b => b.text.trim()).filter(Boolean).join('\n');
      sections.push(otherText);
    }
    
    return sections.join('\n\n');
  };

  // Single canonical insight card upsert helper - ensures exactly ONE insight card per plot draft
  // Uses id = `i_${sid}` for all insight cards, regardless of source
  const upsertInsightCardBySid = (params: {
    sid: string;
    pid: string;
    blocks?: InsightV2Persistent['blocks'];
    markdown?: string;
    source: 'plot_and_insight' | 'initialInsights' | 'chat:message_new';
    execId?: string | null;
    ts?: number;
  }): void => {
    const { sid, pid, blocks, markdown, source, execId, ts } = params;
    
    if (!sid || !pid) {
      try { console.warn('[upsertInsightCardBySid] Missing sid or pid', { sid, pid }); } catch {}
      return;
    }
    
    // Compute markdown content from blocks or use provided markdown
    let insightContent = '';
    if (markdown) {
      insightContent = markdown.trim();
    } else if (blocks && blocks.length > 0) {
      insightContent = blocksToMarkdown(blocks);
    }
    
    if (!insightContent) {
      // No content to display - remove existing insight card if present
      const insightId = `i_${sid}`;
      setMessages(prev => prev.filter(m => m.id !== insightId));
      return;
    }
    
    const insightId = `i_${sid}`;
    const timestamp = ts ? new Date(ts) : new Date();
    
    // Debug logging (behind flag)
    if ((import.meta as any).env?.VITE_DEBUG_INSIGHTS) {
      try {
        console.log('[upsertInsightCardBySid]', {
          sid,
          pid,
          source,
          execId: execId || null,
          blocksCount: blocks?.length || 0,
          contentLength: insightContent.length,
        });
      } catch {}
    }
    
    setMessages(prev => {
      const next = [...prev];
      
      // Find existing insight card with this id
      const existingIdx = next.findIndex(m => m.id === insightId);
      
      const card: ChatMessage = {
        id: insightId,
        role: 'assistant',
        content: insightContent,
        timestamp,
        asMarkdown: true,
        hideAvatar: true,
      } as any;
      
      if (existingIdx >= 0) {
        // Replace existing card in place (preserve ordering)
        next[existingIdx] = card;
      } else {
        // Insert new card - place after the success bubble message for this sid, or after last user message, or at end
        const successBubbleId = `success_${sid}`;
        const successBubbleIdx = next.findIndex(m => m.id === successBubbleId);
        if (successBubbleIdx >= 0) {
          // Insert right after the success bubble
          next.splice(successBubbleIdx + 1, 0, card);
        } else {
          // Fallback: place after last user message, or at end if no user messages
          const lastUserIdx = [...next].reverse().findIndex(m => m.role === 'user');
          if (lastUserIdx >= 0) {
            const insertIdx = next.length - lastUserIdx;
            next.splice(insertIdx, 0, card);
          } else {
            next.push(card);
          }
        }
      }
      
      // Dev-only invariant: ensure exactly ONE card with this id
      const duplicates = next.filter(m => m.id === insightId);
      if (duplicates.length > 1) {
        try {
          console.error('[upsertInsightCardBySid] INVARIANT VIOLATION: Found duplicate insight cards', {
            sid,
            pid,
            source,
            count: duplicates.length,
          });
        } catch {}
        // Collapse: keep the newest (last one), remove the rest
        let keepIdx = -1;
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].id === insightId) {
            keepIdx = i;
            break;
          }
        }
        const filtered = next.filter((m, idx) => m.id !== insightId ? true : idx === keepIdx);
        return filtered;
      }
      
      return next;
    });
  };
  
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    // Initialize with greeting only - insights will be seeded via upsertInsightCardBySid
    return [{ id: '1', role: 'assistant', content: 'Hi! I\'m Lumi, your AI visualization assistant. Upload data and describe what you\'d like to visualize!', timestamp: new Date() }];
  });
  
  // Note: We no longer clear insight cards when plotId changes.
  // Previous insights remain visible so users can review them.
  // Each plot draft (sid) has its own insight card with id `i_${sid}`.

  // Seed insights when initialInsights changes (e.g., when plot is opened)
  useEffect(() => {
    // Get current plotId from route or store
    const currentPlotId = String(routeDraftId || (currentPlot as any)?.id || '');
    const currentProjectId = String(currentProject?.id || '');
    
    if (!initialInsights || !initialInsights.blocks || initialInsights.blocks.length === 0) {
      return; // No insights to seed
    }
    
    // CRITICAL: Validate that insights belong to the current plot
    const insightsPlotId = String(initialInsights.plotId || '');
    if (currentPlotId && insightsPlotId && insightsPlotId !== currentPlotId) {
      try { console.warn('[lumi-chat] insights plotId mismatch, ignoring insights', { insightsPlotId, currentPlotId }); } catch {}
      return; // Don't seed insights that don't belong to current plot
    }
    
    if (!currentPlotId || !currentProjectId) {
      return; // Can't seed without valid plot/project IDs
    }
    
    // Guard: prevent re-seeding the same sid unless content actually changed
    const sid = insightsPlotId || currentPlotId;
    const contentHash = JSON.stringify(initialInsights.blocks.map(b => ({ kind: b.kind, text: b.text })));
    const seedKey = `${sid}:${contentHash}`;
    
    if (seededSidRef.current.has(seedKey)) {
      // Already seeded this exact content for this sid - skip
      return;
    }
    
    try { console.info('[lumi-chat] seeding insights from initialInsights', { blocksCount: initialInsights.blocks.length, plotId: insightsPlotId, currentPlotId }); } catch {}
    
    // Use single canonical upsert helper
    upsertInsightCardBySid({
      sid,
      pid: currentProjectId,
      blocks: initialInsights.blocks,
      source: 'initialInsights',
      execId: initialInsights.runId || null,
      ts: initialInsights.generatedAt ? new Date(initialInsights.generatedAt).getTime() : undefined,
    });
    
    // Mark as seeded
    seededSidRef.current.add(seedKey);
    
    // Cleanup: remove old seed keys for this sid to prevent memory leak
    const keysToRemove: string[] = [];
    seededSidRef.current.forEach(key => {
      if (key.startsWith(`${sid}:`) && key !== seedKey) {
        keysToRemove.push(key);
      }
    });
    keysToRemove.forEach(key => seededSidRef.current.delete(key));
  }, [routeDraftId, currentPlot?.id, initialInsights, currentProject?.id]);
  const [inputValue, setInputValue] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [pickerMode, setPickerMode] = useState<'choose'|'upload'>('choose');
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [isChoosingDataset, setIsChoosingDataset] = useState(false);
  // isProcessing provided by useRunGuards
  const [isTyping, setIsTyping] = useState(false);
  const [dataFingerprintId, setDataFingerprintId] = useState<string | null>(null);
  // progressState removed (unused)
  const [loadingStage, setLoadingStage] = useState<null | 'recipe' | 'cooking' | 'checking'>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [progressLabel, setProgressLabel] = useState<string>(''); // Store the computed label (Creating recipes, Collecting ingredients, etc.)
  const [lastProgressBeatAt, setLastProgressBeatAt] = useState<number>(0);
  const [altStepIndex, setAltStepIndex] = useState<number>(0);
  const [currentAgent, setCurrentAgent] = useState<string>(''); // Track current agent for rotation logic
  const [showSendSpinner, setShowSendSpinner] = useState(false);
  const sendSpinnerStartRef = useRef<number | null>(null);
  const MIN_SEND_SPIN_MS = 300;

  const stopSendSpinner = () => {
    const startedAt = sendSpinnerStartRef.current;
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const elapsed = startedAt ? (now - startedAt) : MIN_SEND_SPIN_MS;
    const remaining = Math.max(0, MIN_SEND_SPIN_MS - elapsed);
    window.setTimeout(() => {
      setShowSendSpinner(false);
      sendSpinnerStartRef.current = null;
    }, remaining);
  };
  const [dotTick, setDotTick] = useState(0);
  const [askUserQuestion, setAskUserQuestion] = useState<string | null>(null);
  const [, setBuilderOpen] = useState(true);
  const [showBuilder, setShowBuilder] = useState(true);
  const [showDirtyConfirm, setShowDirtyConfirm] = useState(false);
  const pendingSendRef = useRef<(() => Promise<void>) | null>(null);
  const [isDropUploading, setIsDropUploading] = useState(false);
  const [isSampleDataUploading, setIsSampleDataUploading] = useState(false);
  const [chartCategory] = useState<string>('Compare');
  const [allCharts, setAllCharts] = useState<any[]>([]);
  const [seeAllCharts, setSeeAllCharts] = useState(false);
  const [pickedChartId, setPickedChartId] = useState<string | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [builderBindings, setBuilderBindings] = useState<BuilderBindings>({});
  
  // Reset builder state when dataset (fingerprint) changes
  // This ensures builder doesn't carry over state from previous dataset
  useEffect(() => {
    if (dataFingerprintId) {
      // Reset builder state for new dataset
      setPickedChartId(null);
      setBuilderBindings({});
      lastBuilderTextRef.current = '';
      // Reset user-touched flag for new dataset (allows builder to seed prompt)
      userTouchedInputRef.current.delete(dataFingerprintId);
    } else {
      // No dataset selected, reset everything
      setPickedChartId(null);
      setBuilderBindings({});
      lastBuilderTextRef.current = '';
    }
  }, [dataFingerprintId]);
  
  const [pendingModify, setPendingModify] = useState<null | {
    messageId: string;
    basePlan: any;
    baseLabel: 'Recommended' | 'Option 2' | 'Option 3';
    planId: string;
  }>(null);
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false);
  const [modifyCtx, setModifyCtx] = useState<null | {
    messageId: string;
    basePlan: any;
    baseLabel: 'Recommended' | 'Option 2' | 'Option 3';
    planId: string;
    selectedProposal?: any;
  }>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const summaryContainerRef = useRef<HTMLDivElement | null>(null);
  const nextFingerprintToScrollRef = useRef<string | null>(null);
  const scrolledFingerprintIdsRef = useRef<Set<string>>(new Set());
  const skipNextAutoScrollRef = useRef<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const postedKeysRef = useRef<Set<string>>(new Set());
  const processedStepsRef = useRef<Set<string>>(new Set());
  const processedRunKeysRef = useRef<Set<string>>(new Set()); // Track canonical run keys for insight dedup
  const seededSidRef = useRef<Set<string>>(new Set()); // Track which sids have been seeded to prevent re-seeding loops
  // Avatar visibility and sizing
  const { latestAssistantId, avatarSize } = useLatestLumiAvatar(messages);

  // Stage helpers for status chip
  const setStageRecipe = () => { setLoadingStage('recipe'); if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[progress] planning'); };
  const setStageCooking = () => { setLoadingStage('cooking'); if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[progress] executing'); };
  const setStageChecking = () => { setLoadingStage('checking'); if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[progress] checking'); };
  const clearStage = () => { setLoadingStage(null as any); setProgressMessage(null); setProgressLabel(''); setCurrentAgent(''); };

  // Build draft claim only when both draftId and claimToken are present
  const buildDraftClaim = () => {
    const did = boundDraftIdRef.current || routeDraftId || null;
    const tok = claimTokenRef.current || null;
    return (did && tok) ? { draftId: did, claimToken: tok } : {};
  };

  // Deduplicated code export trigger per (projectId, plotId)
  const codegenTriggeredRef = useRef<Set<string>>(new Set());
  async function triggerExportCode(projectId: string, plotId: string, figJson?: any) {
    const key = `${projectId}:${plotId}`;
    if (!projectId || !plotId) return;
    if (codegenTriggeredRef.current.has(key)) return;
    codegenTriggeredRef.current.add(key);
    try {
      const res = await authorizedFetch(`/api/ai/v2/export-code?projectId=${encodeURIComponent(projectId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plotId, format: 'python', currentPlotlyJson: figJson })
      });
      if (!res || !(res as any).ok) return;
      const j = await (res as any).json();
      const codeText = String(j?.code || '');
      if (!codeText) return;
      setLiveCodes({ pythonCode: codeText, source: 'draft' });
      setCurrentPlotArtifacts({ id: plotId, pythonCode: codeText, artifactSource: 'draft' } as any);
    } catch {}
  }

  // Chip state derived from WS events; drives stage helpers via monotonic advancement
  const [chip, setChip] = useState<ChipState | null>(null);
  const chipRef = useRef<ChipState | null>(null);
  useEffect(() => { chipRef.current = chip; }, [chip]);
  const applyChip = (next: ChipState, event?: any) => {
    setChip((prev) => {
      const resolved = advanceMonotonic(prev ?? null, next, event);
      if (resolved === 'creating-recipes') setStageRecipe();
      else if (resolved === 'cooking') setStageCooking();
      else if (resolved === 'tasting') setStageChecking();
      else if (resolved === 'done' || resolved === 'failed') clearStage();
      return resolved;
    });
  };

  // mirror processing flag in a ref to avoid stale closures in socket handlers
  const isProcessingRef = useRef<boolean>(isProcessing);
  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);
  const terminalSeenRef = useRef<boolean>(false);
  const activeRunIdRef = useRef<string | null>(null);
  const markTerminal = () => {
    terminalSeenRef.current = true;
    activeRunIdRef.current = null;
    setChip(null);
    setIsProcessing(false);
    execInFlightRef.current = false;
    clearStage();
    setIsTyping(false);
  };

  // --- Run-state reset so new progress pulses aren't ignored
  const resetRunState = (reason?: string) => {
    terminalSeenRef.current = false;
    activeRunIdRef.current = null;
    setChip(null);
    if ((import.meta as any).env?.VITE_NAV_DEBUG) {
      console.log('[progress] resetRunState', { reason });
    }
  };

  // When we flip into a new run, clear the terminal latch
  const prevProcessingRef = useRef<boolean>(isProcessing);
  useEffect(() => {
    if (!prevProcessingRef.current && isProcessing) {
      resetRunState('isProcessing->true');
    }
    prevProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Forward-only stage setter removed (unused)
  // Deduplicate errors across multiple event paths
  const errorKeysRef = useRef<Set<string>>(new Set());
  const mkErrKey = (evt: any) => {
    try {
      const pid = String(evt?.projectId ?? evt?.payload?.projectId ?? '');
      const sid = String(evt?.plotId ?? evt?.stepId ?? evt?.payload?.plotId ?? evt?.payload?.stepId ?? '');
      const rid = String(evt?.runId  ?? evt?.payload?.runId  ?? '');
      const code= String(evt?.error?.code ?? evt?.code ?? '').toUpperCase();
      const msg = String(evt?.error?.message ?? evt?.message ?? '').trim();
      return `${pid}:${sid}:${rid}:${code}:${msg.length}:${msg.slice(0,32)}`;
    } catch { return String(Date.now()); }
  };
  // Dedup for chat:message_new events
  const seenChatMsgIdsRef = useRef<Set<string>>(new Set());
  const surfaceErrorOnce = (evt: any) => {
    const key = mkErrKey(evt);
    if (errorKeysRef.current.has(key)) return;
    errorKeysRef.current.add(key);
    const errText = String(evt?.error?.message || evt?.message || 'Run failed').trim();
    const hint    = String(evt?.error?.hint || '').trim();
    setMessages(prev => [...prev, {
      id: Date.now().toString() + '_error',
      role: 'assistant',
      content: [errText, hint].filter(Boolean).join(' '),
      timestamp: new Date(),
    }]);
  };
  function handleStructuredApiError(result: any): boolean {
    if (!result || (result.status !== 'error' && !result?.error)) return false;
    setIsProcessing(false);
    const cls = String(result.error_class || result.error_code || 'FAILED').toUpperCase();
    const errorMsg = String(result.message || '').toLowerCase();
    const isStorageError = cls === 'STORAGE_CONNECTION_ERROR' || cls === 'STORAGE_ERROR' || 
                           cls === 'STORAGEUNKNOWNERROR' || cls === 'FETCH_FAILED' ||
                           errorMsg.includes('failed to connect to storage') || 
                           errorMsg.includes('fetch failed') || errorMsg.includes('connect timeout');
    
    const message = (() => {
      if (isStorageError) return 'Failed to connect to storage';
      if (cls === 'EMPTY_FIGURE') return 'Lumi couldn\'t render this plot. No credits were used.';
      if (cls === 'ZERO_CREDIT') return 'We couldn\'t verify credits for this run. No credits were used.';
      return 'Plot generation failed';
    })();
    const hint = (() => {
      if (isStorageError) return 'Please try again.';
      if (cls === 'EMPTY_FIGURE') return 'Please try tweaking your prompt or filter. We are constantly improving Lumi\'s capacity to plot. If the issue persists, please let us know to help us improve.';
      if (cls === 'ZERO_CREDIT') return 'Please try again.';
      return 'Please try again with a different prompt or dataset.';
    })();
    surfaceErrorOnce({ error: { message, hint }, projectId: currentProject?.id, plotId: String(boundDraftIdRef.current || routeDraftId || ''), runId: result?.run_id || '' });
    // Ensure the credits badge reflects latest state after terminal errors
    try { if (user?.id) queryClient.invalidateQueries({ queryKey: scopedKey([user?.id, '/api/credits']) as any }); } catch {}
    return true;
  }
  
  // WebSocket connection for real-time updates
  // Drop connection state (unused)
  const [, setLocation] = useLocation();
  const { data: credits } = useCredits(60_000);
  const [billingOpen, setBillingOpen] = useState(false);
  const [forceGate, setForceGate] = useState(false);
  const gateShownRef = useRef(false);

  // Ensure the data summary placeholder + builder wrapper messages are present for a fingerprint
  function ensureSummaryPlaceholder(fingerprintId: string, fp: any) {
    try { (window as any).__last_fp_for_chat = fp; } catch {}
    const introId = `${fingerprintId}_fp_intro`;
    const cardId = `${fingerprintId}_fp_card`;
    setMessages((prev) => {
      const hasIntro = prev.some((m) => m.id === introId);
      const hasCard = prev.some((m) => m.id === cardId);
      if (hasIntro && hasCard) return prev;
      const next = prev.slice();
      if (!hasIntro) next.push({ id: introId, role: 'assistant', content: "Here is a quick summary of your data:", timestamp: new Date(), asMarkdown: true } as any);
      if (!hasCard) next.push({ id: cardId, role: 'assistant', content: '', timestamp: new Date(), asMarkdown: false } as any);
      // Mark this fingerprint to scroll summary into view (first appearance)
      try {
        const id = String(fingerprintId);
        if (id && !scrolledFingerprintIdsRef.current.has(id)) {
          nextFingerprintToScrollRef.current = id;
          skipNextAutoScrollRef.current = true; // suppress immediate bottom scroll once
        }
      } catch {}
      return next;
    });
  }

  // Clean-room draft binding
  const tabIdRef = useRef<string>((() => {
    try {
      const prev = (window as any).__lumiplotTabId;
      if (prev && typeof prev === 'string') return prev;
    } catch {}
    const id = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    try { (window as any).__lumiplotTabId = id; } catch {}
    return id;
  })());
  const boundDraftIdRef = useRef<string | null>(routeDraftId || null);
  const claimTokenRef = useRef<string | null>(null);
  useEffect(() => {
    boundDraftIdRef.current = routeDraftId || null;
    try {
      const map = (window as any).__lumiplotClaims || {};
      if (routeDraftId && typeof map[routeDraftId] === 'string') {
        claimTokenRef.current = map[routeDraftId];
      }
    } catch {}
  }, [routeDraftId]);

  // Dataset reference helper for API payloads
  const getDatasetRef = (): { dataset_id: string } | null => {
    try {
      const id = String(dataFingerprintId || '').trim();
      if (id) return { dataset_id: id } as any;
    } catch {}
    return null;
  };

  // Refactored: claimDraftId returns { pid, sid } with no UI side-effects
  const claimDraftId = async (forceNew: boolean): Promise<{ pid: string; sid: string; claimToken: string } | null> => {
    const pid = currentProject?.id || routePid || undefined;
    if (!pid || !user?.id) return null;
    
    // If not forcing new and we already have a route draft, return it
    if (!forceNew && routeDraftId) {
      const token = (() => { try { return (window as any).__lumiplotClaims?.[routeDraftId] || null; } catch { return null; } })();
      if (token) return { pid, sid: routeDraftId, claimToken: token };
    }
    
    // Create or claim a new draft
    try {
      const idempotencyKey = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const resp = await apiRequest('POST', '/api/plots/create-or-claim', { projectId: pid, tabId: tabIdRef.current, idempotencyKey });
      if (!resp.ok) return null;
      const body = await resp.json();
      const draftId = String(body?.draftId || body?.plotId || body?.id || '');
      const claimToken = String(body?.claimToken || '');
      if (!draftId || !claimToken) return null;
      
      try {
        (window as any).__lumiplotClaims = (window as any).__lumiplotClaims || {};
        (window as any).__lumiplotClaims[draftId] = claimToken;
      } catch {}
      
      return { pid, sid: draftId, claimToken };
    } catch { return null; }
  };

  // Refactored: bindDraftToRoute updates route + refs and optionally resets transient state
  const bindDraftToRoute = async (pid: string, sid: string, claimToken: string, options: { resetChat: boolean }): Promise<void> => {
    try {
      claimTokenRef.current = claimToken;
      boundDraftIdRef.current = sid;
      
      // Reset chat/session state if requested
      if (options.resetChat) {
        const hasFpUi = messages.some(m => /_fp_(intro|card)$/.test(String(m.id))) || !!dataFingerprintId || !!selectedFileId;
        const shouldPreserveUserMsg = isProcessing || execInFlightRef.current;
        if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[reset]', { hasFpUi, shouldPreserveUserMsg, resetChat: options.resetChat, reason: 'bindDraftToRoute' });
        if (!hasFpUi) {
          resetForNewDraft(false, shouldPreserveUserMsg);
        } else {
          resetForNewDraft(true, shouldPreserveUserMsg);
        }
      }
      
      // Bind UI and navigate
      try { setCurrentPlot({ id: sid, projectId: pid, title: 'Draft (in progress)', plotlyJson: { data: [], layout: {} }, pythonCode: null, createdAt: new Date(), creditsConsumed: 0, artifactSource: 'draft' } as any); } catch {}
      // Avoid calling hooks in async; use History API to notify Wouter
      try {
        window.history.pushState({}, '', `/plot-editor/${pid}/${sid}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch {}
      try {
        const ack = await subscribeProject(pid, sid);
        if (ack === 'ok') {
          try { setSubscriptionAck(pid); } catch {}
        }
      } catch {}
    } catch {}
  };

  // Legacy wrapper for backward compatibility (used by Start New Plot CTA)
  const ensureBoundDraft = async (forceCleanReset?: boolean): Promise<{ draftId: string; claimToken: string } | null> => {
    const result = await claimDraftId(!!forceCleanReset);
    if (!result) return null;
    await bindDraftToRoute(result.pid, result.sid, result.claimToken, { resetChat: true });
    return { draftId: result.sid, claimToken: result.claimToken };
  };

  // Expose handshake globally for CTA
  useEffect(() => {
    try { (window as any).__lumiEnsureBoundDraft = ensureBoundDraft; } catch {}
  }, []);


  const resetForNewDraft = (preserveFingerprint?: boolean, preserveUserMessage?: boolean) => {
    try {
      if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[reset]', { preserveFingerprint: !!preserveFingerprint, preserveUserMessage: !!preserveUserMessage });
      setMessages((prev) => {
        // If initialInsights is present, seed messages from that instead of greeting
        // BUT only if insights belong to the current plot
        // initialInsights seeding is now handled by the useEffect hook using upsertInsightCardBySid
        // No need to seed here during reset - the effect will handle it
        
        const greeting = [{ id: '1', role: 'assistant', content: 'Hi! I\'m Lumi, your AI visualization assistant. Upload data and describe what you\'d like to visualize!', timestamp: new Date() } as any];
        if (!preserveFingerprint) return greeting;
        const keep = prev.filter((m) => /_fp_(intro|card)$/.test(String(m.id)));
        const uniq = keep.filter((m, i) => keep.findIndex((x) => String(x.id) === String(m.id)) === i);
        // Ensure intro before card
        uniq.sort((a, b) => String(a.id).endsWith('_fp_intro') ? -1 : (String(b.id).endsWith('_fp_intro') ? 1 : 0));
        // When preserving fingerprint, also preserve the most recent user message if requested
        // This prevents user messages from disappearing during CONFLICT_TOKEN retry
        const recentUserMsg = (preserveUserMessage || isProcessing) ? [...prev].reverse().find(m => m.role === 'user') : null;
        const result = [...greeting, ...uniq];
        if (recentUserMsg && !result.some(m => m.role === 'user' && m.content === recentUserMsg.content)) {
          result.push(recentUserMsg);
        }
        return result;
      });
      postedKeysRef.current.clear();
      processedStepsRef.current.clear();
      execInFlightRef.current = false;
      setAskUserQuestion(null);
      setLoadingStage(null);
      setIsProcessing(false);
      setIsTyping(false);
      setInputValue('');
      if (!preserveFingerprint) setDataFingerprintId(null);
      // Reset builder UI state to defaults for a new draft
      if (!preserveFingerprint) {
        setShowBuilder(true);
        setBuilderOpen(true);
      }
      // Preserve selected chart when preserving fingerprint/analysis context
      if (!preserveFingerprint) setPickedChartId(null);
      setSeeAllCharts(false);
      if (!preserveFingerprint) setSelectedFileId(null as any);
      // Do not clear store fingerprint unless explicitly switching datasets elsewhere.
      // Invalidate plot artifacts only; keep fingerprint context until a new dataset is chosen or cleared explicitly
      if (!preserveFingerprint) {
        try { clearCurrentPlotArtifacts(['pythonCode','javascriptCode','statsJson'] as any); } catch {}
      }
      // Notify other panels (e.g., DataTab) to clear local caches immediately
      if (!preserveFingerprint) {
        try { window.dispatchEvent(new CustomEvent('lumi:sessionReset')); } catch {}
      }
      // Optional: if in future we want to keep summaries across drafts, re-seed placeholders BEFORE wiping globals
    } catch {}
  };

  // Load config to get cost per plot
  const { data: config } = useQuery<any>({
    queryKey: ['/api/config'],
    queryFn: async () => {
      const res = await authorizedFetch('/api/config');
      if (!res.ok) return {} as any;
      return res.json();
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const COST = Math.max(1, Number(config?.credits?.costPerPlot ?? 3) || 3);
  // Check if credits are enabled and remaining is less than cost
  const insufficientServer = !!(credits?.enabled && credits?.remaining !== undefined && credits.remaining < COST);
  const insufficient = !!(insufficientServer || forceGate);
  
  // Track when a run completes successfully - gate should only show AFTER results are displayed
  const runCompletedRef = useRef<boolean>(false);
  const prevIsProcessingRef = useRef<boolean>(isProcessing);
  // Track if current run started with sufficient credits (to allow seeing results before gate shows)
  const runStartedWithSufficientCreditsRef = useRef<boolean>(false);
  
  // Detect when processing starts - check if credits were sufficient at start
  useEffect(() => {
    if (!prevIsProcessingRef.current && isProcessing) {
      // Run is starting - check if credits were sufficient at start
      const hadSufficientCredits = !insufficientServer;
      runStartedWithSufficientCreditsRef.current = hadSufficientCredits;
      runCompletedRef.current = false;
    }
    prevIsProcessingRef.current = isProcessing;
  }, [isProcessing, insufficientServer]);
  
  // Detect when processing completes (transition from true to false)
  useEffect(() => {
    if (prevIsProcessingRef.current && !isProcessing) {
      // Processing just completed - mark that a run has finished
      runCompletedRef.current = true;
    }
  }, [isProcessing]);

  // Telemetry: when gate becomes visible
  useEffect(() => {
    if (insufficient && !gateShownRef.current) {
      gateShownRef.current = true;
      try { metricsInc('credits_gate_shown', { place: 'chat' }); } catch {}
    }
    // If server credits recover (poll refresh), clear any previous forced gate
    if (!insufficientServer && forceGate) setForceGate(false);
    if (!insufficient) gateShownRef.current = false;
  }, [insufficient, insufficientServer, forceGate]);
  
  // Gate logic:
  // 1. Show gate if credits < COST AND not processing AND (no run started OR run completed)
  // 2. Don't show gate DURING processing if run started with sufficient credits (let user see results)
  // 3. Show gate AFTER run completes if credits dropped below threshold
  const shouldShowGate = insufficient && !isProcessing && (
    !runStartedWithSufficientCreditsRef.current || // Run didn't start with sufficient credits, or no run yet
    runCompletedRef.current // Run completed, now safe to show gate
  );

  // Determine pid from store or route for cold starts
  const pid = currentProject?.id || routePid || undefined;
  const filesEnabled = !!pid && !!user;
  // Load project files and fingerprints (trigger on cold deep links)
  const { data: files = [] } = useQuery<FileObject[]>({
    queryKey: scopedKey(['files', user?.id || '', pid || 'none']),
    queryFn: async () => {
      const res = await authorizedFetch(`/api/projects/${pid}/files`);
      return res.json();
    },
    enabled: filesEnabled,
  });
  const noDatasets = !!filesEnabled && Array.isArray(files) && files.length === 0;

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get proper scrollHeight
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const newHeight = Math.min(scrollHeight, 200);
      textareaRef.current.style.height = newHeight + 'px';
      
      // Add scrollbar only when content exceeds max height
      if (scrollHeight > 200) {
        textareaRef.current.style.overflowY = 'auto';
      } else {
        textareaRef.current.style.overflowY = 'hidden';
      }
    }
  }, [inputValue]);

  // Load chart catalog once
  useEffect(() => {
    (async () => {
      try {
        const items = await loadCharts();
        setAllCharts(items);
      } catch {}
    })();
  }, []);

  // WebSocket event handlers + project subscription and guard with ack gating
  useEffect(() => {
    const socket = socketSingleton;
    if (!socket) return;
    // Note: activeUserId local var removed to avoid unused warning; rely on user?.id directly
    const activeProjectId = String(currentProject?.id || '');
    const activeDraftId = String(routeDraftId || '');

    // postOnce intentionally removed; insights are posted by plot_and_insight handler only

    // postTakehome removed (unused)

    // Phase-3: Supervisor progress events stepper mapping
    // setProgressStep removed (unused)

    type RunProgress = {
      kind?: 'progress';
      run_id?: string;
      // legacy field from earlier clients
      stage?: 'planning' | 'executing' | 'checking';
      // normalized fields
      agent?: 'orchestrator' | 'planner' | 'codegen' | 'sandbox' | 'insight' | 'qa' | string;
      phase?: 'analysis:plan' | 'exec:start' | 'exec:done' | 'eval:start' | 'eval:done' | 'analysis:complete' | string;
      message?: string | null;
      percent?: number;
      projectId?: string;
      userId?: string;
    };

    // stageFromProgress removed (unused)

    const handleSupervisorProgress = (payload: RunProgress) => {
      if (terminalSeenRef.current) return;
      const rid = String((payload as any)?.run_id || (payload as any)?.runId || '');
      if (rid) {
        if (!activeRunIdRef.current) {
          activeRunIdRef.current = rid; // latch on first pulse of the run
        } else if (activeRunIdRef.current !== rid) {
          return; // different run → ignore
        }
      }
      if (isForeign(payload)) {
        try { console.info('[ws] drop foreign analysis:*', { evtPid: (payload as any)?.projectId, curPid: activeProjectId }); } catch {}
        try { metricsInc('ws.events_dropped_client', { reason: (!activeProjectId ? 'cold_start' : 'foreign'), evtProjectId: (payload as any)?.projectId, currentProjectId: activeProjectId } as any); } catch {}
        return;
      }
      if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[progress]', payload);
      setIsTyping(true);
      
      // Determine display label based on agent and message
      const agent = String((payload as any)?.agent || '');
      const msg = String((payload as any)?.message || '').trim();
      
      // Update agent tracking and progress beat timestamp
      setCurrentAgent(agent);
      // Only update beat timestamp for codegen (for rotation logic)
      if (agent === 'codegen') {
        setLastProgressBeatAt(Date.now());
        setAltStepIndex(0);
      }
      
      try { trackClient('progress_event', { stage: (payload as any)?.stage }); } catch {}
      // Map to chip and update UI via monotonic advancement; also update progress subtitle
      try {
        const nextChip = mapToChip(payload as any);
        applyChip(nextChip, payload as any);
        let label = '';
        
        // Use nextChip (the chip state we're transitioning to) for immediate accuracy
        // If agent is sandbox, we're definitely in cooking stage
        if (agent === 'sandbox' || nextChip === 'cooking') {
          label = 'Cooking';
          // Clear progressMessage for cooking - we only show "Cooking", no additional message
          setProgressMessage(null);
        } else if (nextChip === 'tasting') {
          label = 'Tasting';
          // Clear progressMessage for tasting - we only show "Tasting", no additional message
          setProgressMessage(null);
        } else if (nextChip === 'creating-recipes') {
          // Planner/orchestrator use "Creating recipes", codegen uses message or "Collecting ingredients"
          if (agent === 'orchestrator' || agent === 'planner') {
            label = 'Creating recipes';
          } else if (agent === 'codegen') {
            label = msg || 'Collecting ingredients';
          } else {
            label = 'Creating recipes';
          }
          // Only set progressMessage if there's a different message than the label
        setProgressMessage(label && msg && msg.toLowerCase() !== label.toLowerCase() ? msg : null);
        } else {
          // Fallback
          label = 'Creating recipes';
          setProgressMessage(null);
        }
        setProgressLabel(label);
      } catch {}
      // Percent overlay removed; we only map text stages
    };

    const isForeign = (evt: any): boolean => {
      try {
        if (!currentProject?.id) return true;
        const pid = String(evt?.projectId || evt?.payload?.projectId || '');
        const uid = String(evt?.userId || evt?.payload?.userId || '');
        // Require project match; userId is optional (enforce only when present)
        if (!pid) return true;
        if (pid !== String(currentProject.id)) return true;
        if (uid && uid !== String(user?.id || '')) return true;
        // Do NOT require draft/step id to match. Route may lag pushState.
        // Use bound draft as a soft hint, but never drop solely on mismatch.
        // If we ever need to hard-drop, only do it when BOTH sides are present and belong to a different project/user (already handled above).
      } catch {}
      return false;
    };

    const handlePlotComplete = (data: any) => {
      if (isForeign(data)) {
        try { console.info('[ws] drop foreign plot:complete', { evtPid: data?.projectId, curPid: activeProjectId }); } catch {}
        try { metricsInc('ws.events_dropped_client', { reason: (!activeProjectId ? 'cold_start' : 'foreign'), event: 'plot:complete', evtProjectId: data?.projectId, currentProjectId: activeProjectId } as any); } catch {}
        return;
      }
      console.log('Plot generation complete:', data);
      markTerminal();
      // Mark that a run has completed successfully - gate can now show if credits are insufficient
      runCompletedRef.current = true;

      let generatedPlot: any = undefined;
      const root0: any = (data?.payload ?? data) || {};
      const stepIdHint = data?.stepId || root0?.stepId || root0?.plotId;
      try {
        if (data?.data?.plots && data.data.plots.length > 0) {
          generatedPlot = data.data.plots[0];
        } else {
          // Normalize unified/compat events (no plots array)
          const root: any = root0;
          const figJson = root?.plot?.figJson || root?.data?.figJson || root?.figJson;
          if (figJson) {
            generatedPlot = { id: (data?.stepId || root?.stepId || `plot_${Date.now()}`), plotlyJson: figJson, creditsUsed: 2 };
          }
          // Insight handling occurs in plot_and_insight; skip here to avoid duplicates
          }
      } catch (e) {
        console.warn('[plot:complete] normalize error (ignored)', e);
      }

      if (generatedPlot) {
        const sid = (stepIdHint || generatedPlot.id) as string;
        
        // Create plot object for store with proper plotSpec
        const newPlot = {
          id: generatedPlot.id,
          projectId: currentProject?.id || '',
          title: generatedPlot.title || 'Generated Plot',
          plotSpec: generatedPlot.spec || {
            version: "1.0.0" as const,
            plotType: 'bar',
            data: { source: "", columns: {}, filters: [], aggregations: [] },
            statistics: { tests: [], transformations: [] },
            styling: { template: "minimal" as const, journalPreset: "Nature" as const, customizations: {} }
          },
          plotlyJson: generatedPlot.plotlyJson,
          pythonCode: null,
          createdAt: new Date(),
          creditsConsumed: generatedPlot.creditsUsed || 2,
        } as any;
        
        setCurrentPlot(newPlot);
        // Fire code export for legacy path
        try {
          if (currentProject?.id && newPlot.id) {
            void triggerExportCode(String(currentProject.id), String(newPlot.id), newPlot.plotlyJson);
          }
        } catch {}
        try {
          const runId = String((data as any)?.runId || activeRunIdRef.current || generatedPlot?.id || '');
          const chartType = String((generatedPlot as any)?.spec?.plotType || (newPlot as any)?.plotSpec?.plotType || 'unknown');
          const credits = Number(generatedPlot?.creditsUsed || 0);
          const t0 = runStartMsRef.current.get(String(boundDraftIdRef.current || routeDraftId || '')) || 0;
          const duration = t0 ? Math.round(performance.now() - t0) : 0;
          const points = (() => {
            try {
              const fig: any = (newPlot as any)?.plotlyJson || {};
              const dataArr = Array.isArray(fig?.data) ? fig.data : [];
              let total = 0;
              for (const tr of dataArr) {
                const nxy = Math.max(Array.isArray(tr?.x) ? tr.x.length : 0, Array.isArray(tr?.y) ? tr.y.length : 0);
                let nz = 0;
                const z = tr?.z;
                if (Array.isArray(z)) {
                  if (Array.isArray(z[0])) nz = (z as any[]).reduce((s, row) => s + (Array.isArray(row) ? row.length : 0), 0);
                  else nz = z.length || 0;
                }
                const nvals = Array.isArray(tr?.values) ? tr.values.length : 0;
                total += Math.max(nxy, nz, nvals);
              }
              return total;
            } catch { return 0; }
          })();
          const transformsApplied = (() => {
            try {
              const rec = (lastPlanSpecRef.current as any)?.recommended;
              return Array.isArray(rec?.transforms) ? rec.transforms.length : 0;
            } catch { return 0; }
          })();
          if (runId) {
            lastRunIdByDraftRef.current.set(String(boundDraftIdRef.current || routeDraftId || ''), runId);
            try {
              const pid = String(currentProject?.id || '');
              const did = String(boundDraftIdRef.current || routeDraftId || '');
              if (pid && did) localStorage.setItem(`lumiplot:lastRunId:${pid}:${did}`, runId);
            } catch {}
          }
          if ((import.meta as any).env?.VITE_FE_EMIT_RUN_EVENTS === 'true') {
            (track as any)('client:run:succeeded', {
              run_id: runId,
              duration_ms: duration,
              chart_type: chartType,
              rows_plotted: points,
              transforms_applied: transformsApplied,
              credits_charged: credits,
            });
          }
        } catch {}

        // Add success message + fallback only once per step
        if (sid && processedStepsRef.current.has(sid)) {
          return;
        }
        if (sid) processedStepsRef.current.add(sid);

        // Add success message to chat and immediately append take-home if available
        const msgs: ChatMessage[] = [];
        msgs.push({
          id: Date.now().toString() + '_success',
          role: 'assistant',
          content: `Plot generated successfully! You can see it in the canvas and customize it using the editor.`,
          timestamp: new Date(),
        });
        // Insight is handled in plot_and_insight handler below
        try { /* no-op */ } catch {}
        setMessages(prev => [...prev, ...msgs]);

        // Do not poll for HTTP latest; rely on plot_and_insight event for results

        // Navigation only when initiated by this tab
        try {
          const pid = currentProject?.id || '';
          const initiatedHere = !!execInFlightRef.current;
          if (pid && generatedPlot.id) {
            if (initiatedHere) {
              try { setLocation(`/plot-editor/${pid}/${generatedPlot.id}`); } catch {}
            } else {
              toast({ title: 'Update finished in another tab', description: `Project ${pid}` });
            }
          }
        } catch {}
      }
    };

    const handlePlotError = (data: any) => {
      try {
        if (isForeign(data)) return;
      } catch {}
      console.error('❌ Plot generation error:', data);
      try {
        const runId = String((data as any)?.runId || (data as any)?.run_id || activeRunIdRef.current || '');
        const t0 = runStartMsRef.current.get(String(boundDraftIdRef.current || routeDraftId || '')) || 0;
        const duration = t0 ? Math.round(performance.now() - t0) : 0;
        const msg = String((data?.data?.error || data?.error || 'error')).slice(0, 500);
        hashWeb(msg).then((h) => {
          if ((import.meta as any).env?.VITE_FE_EMIT_RUN_EVENTS === 'true') {
            (track as any)('client:run:failed', {
              run_id: runId,
              duration_ms: duration,
              error_stage: 'client',
              error_class: String((data as any)?.code || 'RUNTIME_ERROR'),
              error_hash: h,
              credits_charged: 0,
            });
          }
        }).catch(() => {
          if ((import.meta as any).env?.VITE_FE_EMIT_RUN_EVENTS === 'true') {
            (track as any)('client:run:failed', {
              run_id: runId,
              duration_ms: duration,
              error_stage: 'client',
              error_class: String((data as any)?.code || 'RUNTIME_ERROR'),
              error_hash: '0'.repeat(64),
              credits_charged: 0,
            });
          }
        });
      } catch {}
      markTerminal();
      surfaceErrorOnce({ error: { message: String(data?.data?.error || 'Plot error'), hint: String(data?.data?.suggestion || '') }, projectId: data?.projectId, plotId: data?.plotId, runId: data?.runId });
    };

    const registerListeners = () => {
      socket.on('analysis:progress', handleSupervisorProgress as any);
      // On reconnect, rehydrate from REST to self-heal stale tabs
      socket.on('reconnect', async () => {
        try {
          const pid = String(currentProject?.id || '');
          const sid = String(routeDraftId || (usePlotsStore.getState().currentPlot as any)?.id || '');
          if (!pid || !sid) return;
          const [figRes, statsRes] = await Promise.all([
            authorizedFetch(`/api/ai/v2/plots/${sid}/versions/latest/plotly_figure.json`).catch(() => ({ ok: false } as any)),
            authorizedFetch(`/api/ai/v2/plots/${sid}/versions/latest/stats.json`).catch(() => ({ ok: false } as any)),
          ]);
          if (figRes && (figRes as any).ok) {
            const fig = await (figRes as any).json();
            try { setCurrentPlotArtifacts({ id: sid, projectId: pid, plotlyJson: fig, artifactSource: 'latest' } as any); } catch {}
          }
          if (statsRes && (statsRes as any).ok) {
            const stats = await (statsRes as any).json();
            try { setLiveStats(stats, 'version'); } catch {}
            try { setCurrentPlotArtifacts({ id: sid, projectId: pid, statsJson: stats, artifactSource: 'latest' } as any); } catch {}
          }
          try { applyLiveToCurrentPlot(); } catch {}
        } catch {}
      });
      // Do not force a stage on analysis:run; wait for proper analysis:progress pulses
      socket.on('analysis:run', (evt: any) => {
        if (terminalSeenRef.current) return;
        if (isForeign(evt)) return;
        // Optional: keep it in 'recipe' if you want immediate feedback
        // setStageMonotonic('recipe');
      });
      // Explicit error channel: debounce; if terminal follows, suppress duplicate
      socket.on('analysis:error', (evt: any) => {
        try {
          // Only gate by user/project; do not require draft match
          const drop = (() => {
            if (!user?.id || !currentProject?.id) return true;
            const pid = String(evt?.projectId || evt?.payload?.projectId || '');
            const uid = String(evt?.userId || evt?.payload?.userId || '');
            if (uid && uid !== String(user.id)) return true;
            if (pid && pid !== String(currentProject.id)) return true;
            return false;
          })();
          if (drop) return;
          setTimeout(() => {
            if (!terminalSeenRef.current) surfaceErrorOnce(evt);
          }, 200);
        } catch {}
      });
      // Legacy compat: listen for plot:complete as well
      socket.on('plot:complete', (evt: any) => {
        try {
          if (isForeign(evt)) return;
          const th = evt?.data?.takehome;
          if (!th || typeof th !== 'object') return;
          const data = safeNormalizeInsightsPayload(th);
          const root0: any = (evt?.payload ?? evt) || {};
          const sid = String(evt?.stepId || root0?.stepId || '');
          const FEATURE_INSIGHT_UPSERT_V2 = true;
          const buildFingerprint = (sid2: string): string => `i_${String(sid2 || '')}`;
          const blocks: string[] = [];
          blocks.push(`**Figure caption**\n${String(data.figure_caption || '').trim()}`);
          const items = Array.isArray(data.insights) ? data.insights : [];
          if (items.length > 0) {
            const results = items.map((it: any) => {
              const t = String(it?.title || '').trim();
              const b = String(it?.body || '').trim();
              return t ? `${t}${b ? `: ${b}` : ''}` : (b ? `${b}` : '');
            }).filter(Boolean).join('\n');
            blocks.push(`**Results**\n${results}`);
          } else {
            blocks.push('**Results**\nNo results available for this figure.');
          }
          const steps: string[] = Array.isArray(data.next_steps) ? data.next_steps.map((s: any) => String(s)).filter(Boolean) : [];
          if (steps.length > 0) {
            blocks.push(`**Next steps**\n${steps.map(s => `${s}`).join('\n')}`);
          } else {
            blocks.push('**Next steps**\nProvide data or adjust mappings to enable analysis.');
          }
          const content = blocks.join('\n\n');
          const fingerprint = FEATURE_INSIGHT_UPSERT_V2 ? buildFingerprint(sid) : `${sid}:${content.length}`;
          setMessages(prev => {
            const next = [...prev];
            const idx = next.findIndex(m => m.id === fingerprint);
            const card = { id: fingerprint, role: 'assistant', content, timestamp: new Date(), asMarkdown: true, hideAvatar: true } as any;
            if (idx >= 0) next[idx] = card; else next.push(card);
            return next;
          });
        } catch {}
        // Keep existing plot handling
        handlePlotComplete(evt);
      });
      // Unified content event
      socket.on('plot_and_insight', (evt: any) => {
      if (isForeign(evt)) {
        try { console.info('[ws] drop foreign plot_and_insight', { evtPid: evt?.projectId, curPid: activeProjectId }); } catch {}
        try { metricsInc('ws.events_dropped_client', { reason: (!activeProjectId ? 'cold_start' : 'foreign'), event: 'plot_and_insight', evtProjectId: evt?.projectId, currentProjectId: activeProjectId } as any); } catch {}
        // Cache for background sync by payload context
        try {
          const cpid = String(evt?.projectId || evt?.payload?.projectId || '');
          const cid = String(evt?.stepId || evt?.plotId || '');
          const fig = evt?.payload?.plot?.figJson;
          if (cpid && cid && fig) {
            usePlotsStore.getState().setPlotByContext({ projectId: cpid, id: cid, data: { plotlyJson: fig, title: `Generated Plot ${new Date().toLocaleTimeString()}`, pythonCode: null, creditsConsumed: 0, createdAt: new Date() } });
          }
        } catch {}
        return;
      }
      try {
        // 🔑 CRITICAL: Emit completion event immediately when backend reports plot_and_insight
        // This ensures TourManager and other subsystems react as soon as the run completes,
        // even if artifacts are still being fetched. This is the primary signal for Step 5.
        const eventProjectId = String(evt?.projectId || evt?.payload?.projectId || currentProject?.id || '');
        const eventPlotId = String(evt?.stepId || evt?.plotId || evt?.payload?.plotId || evt?.payload?.stepId || '');
        const eventPlanId = String(evt?.planId || evt?.payload?.planId || eventPlotId || ''); // planId same as plotId in this context
        
        // Only emit if we have at least projectId and plotId (for non-foreign events)
        if (eventProjectId && eventPlotId && !isForeign(evt)) {
          logEvt('emit:analysis:plot_built:immediate', { 
            projectId: eventProjectId, 
            plotId: eventPlotId, 
            planId: eventPlanId,
            run_id: (evt as any)?.run_id,
            hasFigJson: !!(evt?.payload?.plot?.figJson)
          });
          window.dispatchEvent(new CustomEvent('analysis:plot_built', { 
            detail: { 
              projectId: eventProjectId, 
              plotId: eventPlotId, 
              planId: eventPlanId,
              run_id: (evt as any)?.run_id
            } 
          }));
        }
        
        // Update current plot - handle both figJson present and missing cases
        const fig = evt?.payload?.plot?.figJson;
        const pid = String(currentProject?.id || '');
        const sid = String(evt?.stepId ?? evt?.plotId ?? evt?.payload?.plotId ?? evt?.payload?.stepId ?? '');
        
        if (pid && sid) {
          // Draft artifacts + code export and normalized event
          (async () => {
            let figure = fig;
            let figFetchFailed = false;
            
            // If figJson is missing, fetch it via fallback
            if (!figure) {
              try {
                const figRes = await authorizedFetch(`/api/ai/v2/drafts/${pid}/${sid}/plotly_figure.json`);
                if (figRes && (figRes as any).ok) {
                  figure = await (figRes as any).json();
                } else if ((figRes as any).status === 404) {
                  figFetchFailed = true;
                  // Mark run as terminal and show error toast, but don't spam retries
                  try { console.warn('[plot_and_insight] Figure not found (404) for', { pid, sid }); } catch {}
                  toast({ title: 'Plot unavailable', description: 'The plot figure could not be loaded. Please try again.', variant: 'destructive' });
                }
              } catch (err) {
                figFetchFailed = true;
                try { console.warn('[plot_and_insight] Fallback figure fetch failed', err); } catch {}
              }
            }
            
            // Only proceed if we have a figure (either from payload or successful fallback fetch)
            if (!figure && figFetchFailed) {
              return; // Don't proceed if figure is truly missing
            }
            
            // Fetch stats and meta regardless of whether figJson was initially present
            const [statsRes, metaResPrimary] = await Promise.all([
              authorizedFetch(`/api/ai/v2/drafts/${pid}/${sid}/stats.json`).catch(() => ({ ok: false } as any)),
              authorizedFetch(`/api/ai/v2/drafts/${pid}/${sid}/metadata.json`).catch(() => ({ ok: false } as any)),
            ]);
            const stats = statsRes && (statsRes as any).ok ? await (statsRes as any).json() : null;
            // fallback to legacy meta.json to be backward compatible (single fetch)
            let meta: any = null;
            if (metaResPrimary && (metaResPrimary as any).ok) {
              meta = await (metaResPrimary as any).json();
            } else {
              const metaFallbackRes = await authorizedFetch(`/api/ai/v2/drafts/${pid}/${sid}/meta.json`).catch(() => ({ ok: false } as any));
              if ((metaFallbackRes as any).ok) meta = await (metaFallbackRes as any).json();
            }
            
            // Update plot with figure (from payload or fallback fetch)
            if (figure && currentProject?.id) {
              const newPlot = {
                id: sid,
                projectId: currentProject.id,
                title: `Generated Plot ${new Date().toLocaleTimeString()}`,
                plotlyJson: figure,
                pythonCode: null,
                createdAt: new Date(),
                creditsConsumed: 2,
              } as any;
              setCurrentPlot(newPlot);
              try { setCurrentPlotArtifacts({ id: sid, projectId: String(currentProject.id), plotlyJson: figure, artifactSource: 'draft' }); } catch {}
              const initiatedHere = !!execInFlightRef.current;
              if (initiatedHere) {
                try { setLocation(`/plot-editor/${String(currentProject.id)}/${sid}`); } catch {}
              } else {
                toast({ title: 'Update finished in another tab', description: `Project ${currentProject.id}` });
              }
            }
            
            // Hydrate stats and meta regardless of whether figJson was initially present
            if (stats) {
              setLiveStats(stats, 'draft');
              setCurrentPlotArtifacts({ id: sid, statsJson: stats, artifactSource: 'draft' } as any);
            }
            // Ensure plotlyJson is set even if it came from fallback fetch
            if (figure) {
              setCurrentPlotArtifacts({ id: sid, plotlyJson: figure, artifactSource: 'draft' } as any);
            }
            // Toast on soft-fail when figure is empty/non-renderable (one-time per plot)
            if (figure) {
              try { maybeToastSoftFail(sid, figure); } catch {}
              try { void triggerExportCode(pid, sid, figure); } catch {}
            }
            // Auto-save disabled: keep editor on draft artifacts until user explicitly saves
            try {
              const store = usePlotsStore.getState();
              // Prefer server-provided fingerprint id when present
              const serverFid = String((evt as any)?.payload?.dataFingerprintId || '') || null;
              if (serverFid) {
                try { store.setLiveDataFingerprint(String(pid), String(sid), serverFid, { source: 'ws' }); } catch {}
              }
              // Fallback: read datasetFileId or dataFingerprintId from metadata/meta if present
              const metaFid = (() => {
                try {
                  return String((meta as any)?.datasetFileId || (meta as any)?.dataFingerprintId || '') || null;
                } catch { return null; }
              })();
              if (!serverFid && metaFid) {
                try { store.setLiveDataFingerprint(String(pid), String(sid), metaFid, { source: 'meta' }); } catch {}
              }
              // Final fallback: use current chat selection if available
              const chatFid = (() => { try { return String(dataFingerprintId || '') || null; } catch { return null; } })();
              if (!serverFid && !metaFid && chatFid) {
                try { store.setLiveDataFingerprint(String(pid), String(sid), chatFid, { source: 'chat' }); } catch {}
              }
              const fid = serverFid || metaFid || chatFid || (store.getLiveDataFingerprint?.(String(pid), String(sid)) || store.hydration?.live?.latestDataFingerprintId || (store.currentPlot as any)?.latestDataFingerprintId || null);
              // Note: dataFingerprintId precedence: websocket payload > store context > legacy fields
              // Emit richer payload with artifacts (figure, stats, dataFingerprintId) - this supplements the immediate event above
              const planIdValue = String(evt?.planId || evt?.payload?.planId || sid || '');
              const execId = String((evt as any)?.execId || (evt as any)?.runId || (evt as any)?.payload?.execId || (evt as any)?.payload?.runId || '');
              logEvt('emit:analysis:plot_built:enriched', { projectId: pid, plotId: sid, planId: planIdValue, fid, run_id: execId });
              const payload = { plotId: sid, projectId: pid, planId: planIdValue, figure, stats, dataFingerprintId: fid, run_id: execId } as any;
              window.dispatchEvent(new CustomEvent('analysis:plot_built', { detail: payload }));
              try { usePlotsStore.getState().applyLiveToCurrentPlot(); } catch {}
              // Also notify Data pipeline explicitly so DataTab can hydrate immediately
              try {
                if (fid) {
                  window.dispatchEvent(new CustomEvent('lumi:dataAnalyzed', {
                    detail: { fingerprintId: fid, fileId: fid, projectId: pid, plotId: sid }
                  }));
                }
              } catch {}
            } catch {}
            applyLiveToCurrentPlot();
          })();
        }
        // Build insight message content (structured panel: Caption → Results → Next steps)
        // Use single canonical insight card ID per plot draft: `i_${sid}`
        const root: any = (evt?.payload ?? evt) || {};
        const insightNode = (root?.insight ?? root?.takehome ?? root?.data?.takehome ?? root) || {};
        
        const panelData = safeNormalizeInsightsPayload(insightNode);
        // Use comprehensive plotId extraction: prefer plotId over stepId, check both evt and payload
        const insightSid = String(evt?.plotId ?? evt?.stepId ?? root?.plotId ?? root?.stepId ?? evt?.payload?.plotId ?? evt?.payload?.stepId ?? '');
        const insightExecId = String((evt as any)?.execId || (evt as any)?.runId || (evt as any)?.payload?.execId || (evt as any)?.payload?.runId || '');
        const insightPid = String(currentProject?.id || '');
        
        // Canonical run key for processedRunKeysRef (optional dedup during same exec)
        const canonicalRunKey = insightExecId ? `${insightPid}:${insightSid}:${insightExecId}` : null;
        
        const prebuiltMarkdown = String(root?.insight_markdown || '').trim();
        
        // Convert panelData to blocks format for upsertInsightCardBySid and store
        const blocks: InsightV2Persistent['blocks'] = [];
        
        // Add figure caption as summary block
        if (panelData.figure_caption && String(panelData.figure_caption).trim()) {
          blocks.push({
            id: `caption-${Date.now()}`,
            role: 'assistant',
            kind: 'summary',
            text: String(panelData.figure_caption),
          });
        }
        
        // Add insight cards as note blocks
        if (Array.isArray(panelData.insights) && panelData.insights.length > 0) {
          panelData.insights.forEach((card, idx) => {
            const title = String(card?.title || '').trim();
            const body = String(card?.body || '').trim();
            if (title || body) {
              blocks.push({
                id: `insight-${idx}-${Date.now()}`,
                role: 'assistant',
                kind: 'note',
                text: title ? `${title}${body ? `: ${body}` : ''}` : body,
              });
            }
          });
        }
        
        // Add next steps as suggestion blocks
        if (Array.isArray(panelData.next_steps) && panelData.next_steps.length > 0) {
          panelData.next_steps.forEach((step, idx) => {
            const text = String(step || '').trim();
            if (text) {
              blocks.push({
                id: `next-step-${idx}-${Date.now()}`,
                role: 'assistant',
                kind: 'suggestion',
                text,
              });
            }
          });
        }
        
        // Store insights to artifacts (for persistence)
        try {
          const pid = String(currentProject?.id || '');
          const currentPlotId = usePlotsStore.getState().currentPlot?.id;
          const targetPlotId = (currentPlotId && insightSid && currentPlotId === insightSid) ? currentPlotId : insightSid;
          
          if (pid && targetPlotId && blocks.length > 0) {
            const insightPersistent: InsightV2Persistent = {
              schemaVersion: 'InsightV2',
              projectId: pid,
              plotId: targetPlotId,
              versionId: null, // Draft insights don't have versionId yet
              generatedAt: new Date().toISOString(),
              model: String((root as any)?.model || ''),
              runId: String((evt as any)?.runId || (evt as any)?.run_id || activeRunIdRef.current || ''),
              blocks,
            };
            // Ensure currentPlot exists before storing insights
            const store = usePlotsStore.getState();
            if (!store.currentPlot || store.currentPlot.id !== targetPlotId) {
              // Create a minimal plot if it doesn't exist
              try {
                store.setCurrentPlot({ 
                  id: targetPlotId, 
                  projectId: pid, 
                  title: 'Generated Plot', 
                  plotlyJson: { data: [], layout: {} }, 
                  pythonCode: null, 
                  createdAt: new Date(), 
                  creditsConsumed: 0 
                } as any);
              } catch {}
            }
            setCurrentPlotArtifacts({ id: targetPlotId, projectId: pid, insights: insightPersistent, artifactSource: 'draft' } as any);
            try { console.info('[plot_and_insight] stored insights', { plotId: targetPlotId, blocksCount: blocks.length, hadCurrentPlot: !!currentPlotId }); } catch {}
          }
        } catch (err) {
          // Non-critical: insights storage failure shouldn't break the UI
          try { console.warn('[plot_and_insight] Failed to store insights', err); } catch {}
        }
        
        // Use single canonical upsert helper - ensures exactly ONE insight card per plot draft
        if (insightSid && insightPid && blocks.length > 0) {
          // Optional: skip if we've already processed this exact run (prevents duplicate events)
          if (canonicalRunKey && processedRunKeysRef.current.has(canonicalRunKey)) {
            // Already processed - just update the card in place
            upsertInsightCardBySid({
              sid: insightSid,
              pid: insightPid,
              blocks,
              markdown: prebuiltMarkdown || undefined,
              source: 'plot_and_insight',
              execId: insightExecId || null,
            });
          } else {
            // First time seeing this run - add success bubble and upsert insight
            if (canonicalRunKey) {
              processedRunKeysRef.current.add(canonicalRunKey);
            }
            
            // Add success bubble (only once per step)
            const already = insightSid ? processedStepsRef.current.has(insightSid) : false;
            if (!already && insightSid) {
              setMessages(prev => {
                const next = [...prev];
                next.push({ id: `success_${insightSid}` as any, role: 'assistant', content: `Plot generated successfully! You can see it in the canvas and customize it using the editor.`, timestamp: new Date(), forceAvatar: true, hasPlot: true } as any);
                return next;
              });
              processedStepsRef.current.add(insightSid);
            }
            
            // Upsert insight card using canonical ID
            upsertInsightCardBySid({
              sid: insightSid,
              pid: insightPid,
              blocks,
              markdown: prebuiltMarkdown || undefined,
              source: 'plot_and_insight',
              execId: insightExecId || null,
            });
          }
        }
        markTerminal();
      } catch {}
      });
      // Terminal events
      socket.on('run_finished', (_evt: any) => {
        markTerminal();
      });
      socket.on('run_failed', (evt: any) => {
        try {
          const drop = isForeign(evt);
          if (drop) return;
          // Cold start UX: surface a warming toast for spawn timeouts
          try {
            const code = String(evt?.error?.code || '').toUpperCase();
            if (code === 'TIMEOUT_SPAWN') {
              toast({ title: 'Warming compute', description: 'Cold start detected. Please try again in a moment.' });
            }
          } catch {}
          markTerminal();
          surfaceErrorOnce(evt);
        } catch {}
      });
      // Chat insight injection path
      socket.on('chat:message_new', (evt: any) => {
        try {
          const pid = String(evt?.projectId || '');
          const uid = String(evt?.userId || '');
          if (!pid || !uid) return;
          if (isForeign({ projectId: pid, userId: uid })) return;
          const msg = evt?.message || {};
          if (String(msg?.type) !== 'insight') return;
          // Deduplicate by message id when available
          const mid = String((msg?.id || evt?.messageId || evt?.id || '') || '');
          if (mid) {
            if (seenChatMsgIdsRef.current.has(mid)) return;
            seenChatMsgIdsRef.current.add(mid);
          }
          try { metricsInc('chat.foreign_event_dropped', { dropped: false }); } catch {}
          // Prefer server-provided markdown when present
          const markdown = String(msg?.markdown || '').trim();
          
          const panelData = safeNormalizeInsightsPayload(msg);
          const sid = String(msg?.plotId || msg?.stepId || evt?.plotId || evt?.stepId || '');
          const execId = String(msg?.runId || msg?.execId || evt?.runId || evt?.execId || '');
          
          // Canonical run key for processedRunKeysRef (optional dedup during same exec)
          const canonicalRunKey = execId ? `${pid}:${sid}:${execId}` : null;
          
          // Convert panelData to blocks format for upsertInsightCardBySid and store
          const blocks: InsightV2Persistent['blocks'] = [];
          
          // Add figure caption as summary block
          if (panelData.figure_caption && String(panelData.figure_caption).trim()) {
            blocks.push({
              id: `caption-${Date.now()}`,
              role: 'assistant',
              kind: 'summary',
              text: String(panelData.figure_caption),
            });
          }
          
          // Add insight cards as note blocks
          if (Array.isArray(panelData.insights) && panelData.insights.length > 0) {
            panelData.insights.forEach((card, idx) => {
              const title = String(card?.title || '').trim();
              const body = String(card?.body || '').trim();
              if (title || body) {
                blocks.push({
                  id: `insight-${idx}-${Date.now()}`,
                  role: 'assistant',
                  kind: 'note',
                  text: title ? `${title}${body ? `: ${body}` : ''}` : body,
                });
              }
            });
          }
          
          // Add next steps as suggestion blocks
          if (Array.isArray(panelData.next_steps) && panelData.next_steps.length > 0) {
            panelData.next_steps.forEach((step, idx) => {
              const text = String(step || '').trim();
              if (text) {
                blocks.push({
                  id: `next-step-${idx}-${Date.now()}`,
                  role: 'assistant',
                  kind: 'suggestion',
                  text,
                });
              }
            });
          }
          
          // Store insights to artifacts (for persistence)
          try {
            if (pid && sid && blocks.length > 0) {
              const insightPersistent: InsightV2Persistent = {
                schemaVersion: 'InsightV2',
                projectId: pid,
                plotId: sid,
                versionId: null, // Draft insights don't have versionId yet
                generatedAt: new Date().toISOString(),
                model: String((msg as any)?.model || (evt as any)?.model || ''),
                runId: String((msg as any)?.runId || (evt as any)?.runId || (evt as any)?.run_id || activeRunIdRef.current || ''),
                blocks,
              };
              setCurrentPlotArtifacts({ id: sid, projectId: pid, insights: insightPersistent, artifactSource: 'draft' } as any);
            }
          } catch (err) {
            // Non-critical: insights storage failure shouldn't break the UI
            try { console.warn('[chat:message_new] Failed to store insights', err); } catch {}
          }
          
          // Use single canonical upsert helper - ensures exactly ONE insight card per plot draft
          if (sid && pid && (blocks.length > 0 || markdown)) {
            // Optional: skip if we've already processed this exact run (prevents duplicate events)
            if (canonicalRunKey && processedRunKeysRef.current.has(canonicalRunKey)) {
              // Already processed - just update the card in place
              upsertInsightCardBySid({
                sid,
                pid,
                blocks: blocks.length > 0 ? blocks : undefined,
                markdown: markdown || undefined,
                source: 'chat:message_new',
                execId: execId || null,
                ts: (() => {
                  try { return new Date(String(msg?.createdAt || evt?.createdAt || Date.now())).getTime(); } catch { return undefined; }
                })(),
              });
            } else {
              // First time seeing this run
              if (canonicalRunKey) {
                processedRunKeysRef.current.add(canonicalRunKey);
              }
              
              // Upsert insight card using canonical ID
              upsertInsightCardBySid({
                sid,
                pid,
                blocks: blocks.length > 0 ? blocks : undefined,
                markdown: markdown || undefined,
                source: 'chat:message_new',
                execId: execId || null,
                ts: (() => {
                  try { return new Date(String(msg?.createdAt || evt?.createdAt || Date.now())).getTime(); } catch { return undefined; }
                })(),
              });
            }
          } else {
            // Not an insight message - ignore
            try { metricsInc('chat.fallback_normalize_used', { empty: true }); } catch {}
          }
        } catch {}
      });
      socket.on('plot:error', handlePlotError);
    };

    try { registerListeners(); } catch {}

    // Subscription flow with ack gating; only when bound to a draft
    (async () => {
      try {
        if (activeProjectId && activeDraftId) {
          const prev = (() => { try { return (useProjectsStore.getState() as any).subscribedProjectId || null; } catch { return null; } })();
          if (prev && prev !== activeProjectId) {
            await unsubscribeProject(prev as any, undefined);
          }
          const ack = await subscribeProject(activeProjectId, activeDraftId);
          if (ack === 'ok') {
            try { setSubscriptionAck(activeProjectId); } catch {}
          } else {
            console.warn('[ws] subscribe_project forbidden', { activeProjectId, activeDraftId });
          }
        }
      } catch {}
    })();

    return () => {
      try {
        socket.off('analysis:progress', handleSupervisorProgress as any);
        socket.off('analysis:run');
        socket.off('plot:complete', handlePlotComplete as any);
        socket.off('plot_and_insight');
        socket.off('run_finished');
        socket.off('run_failed');
        socket.off('analysis:error');
        socket.off('chat:message_new');
        socket.off('plot:error', handlePlotError);
      } catch {}
      try {
        const ready = (() => { try { return String((useProjectsStore.getState() as any).subscribedProjectId || '') === String(activeProjectId); } catch { return false; } })();
        if (activeProjectId && ready) {
          try { void unsubscribeProject(activeProjectId, activeDraftId || undefined); } catch {}
          try { setSubscriptionAck(null as any); } catch {}
        }
      } catch {}
    };
  }, [currentProject, setCurrentPlot, user?.id, routeDraftId]);

  // Note: Removed mount-time fetch of latest take-home to avoid unsolicited messages
  // Animate loading dots for stage indicator
  useEffect(() => {
    if (!loadingStage) return;
    const t = setInterval(() => setDotTick((n) => (n + 1) % 4), 500);
    return () => clearInterval(t);
  }, [loadingStage]);

  // Fallback rotation for codegen progress messages when server is quiet
  // Only rotate for codegen agent, not for orchestrator/planner (which also use 'recipe' chip)
  useEffect(() => {
    let interval: any = null;
    const isCodegenChip = loadingStage === 'recipe' && currentAgent === 'codegen';
    if (isCodegenChip) {
      interval = setInterval(() => {
        const quietMs = Date.now() - lastProgressBeatAt;
        if (quietMs > 3000) {
          setAltStepIndex(i => {
            const next = (i + 1) % CODEGEN_MESSAGES_PLAYFUL.length;
            const nextMessage = CODEGEN_MESSAGES_PLAYFUL[next];
            setProgressLabel(nextMessage);
            setProgressMessage(null);
            return next;
          });
        }
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loadingStage, lastProgressBeatAt, currentAgent]);

  // Track fingerprint for reactivity
  const [fingerprintState, setFingerprintState] = useState<any>(() => {
    try {
      return (window as any).__last_fp_for_chat || null;
    } catch {
      return null;
    }
  });

  // Watch for fingerprint updates
  useEffect(() => {
    const checkFingerprint = () => {
      try {
        const fp = (window as any).__last_fp_for_chat || null;
        setFingerprintState(fp);
      } catch {}
    };
    // Check immediately
    checkFingerprint();
    // Poll for changes (fingerprint is set asynchronously)
    const interval = setInterval(checkFingerprint, 500);
    return () => clearInterval(interval);
  }, []);

  // Convert fingerprint to new format
  const fingerprint: Fingerprint | null = useMemo(() => {
    if (!fingerprintState) return null;
    try {
      const cols = fingerprintState.columns || [];
      return {
        rows: fingerprintState.rows,
        columns: cols.map((c: any) => {
          // Handle both string and object formats
          if (typeof c === 'string') {
            return {
              key: c,
              label: c,
            };
          }
          // Object format: extract name/key, label, type, cardinality
          const name = c.name || c.key || '';
          const label = c.label || c.name || c.key || '';
          // Extract type from fingerprint - prioritize actual data analysis results
          // Fingerprint may use "number" instead of "numeric", so we preserve it for normalization in inferType
          const fingerprintType = c.type || c.role_guess || undefined;
          return {
            key: name,
            label: label,
            type: fingerprintType,
            cardinality: c.cardinality ?? c.unique ?? undefined,
            role_guess: c.role_guess || fingerprintType || undefined, // Use role_guess or fallback to type
          };
        }).filter((col: any) => col.key), // Filter out empty keys
        suggestions: fingerprintState.suggestions,
      };
    } catch {
      return null;
    }
  }, [fingerprintState]);

  // Get column metadata (for ChartBuilderCard compatibility)
  const columnMetaByName = useMemo(() => {
    const out: Record<string, any> = {};
    try {
      const cols = fingerprintState?.columns || [];
      for (const c of cols) {
        if (!c) continue;
        const n = (typeof c === 'string' ? c : c.name) || '';
        if (n) {
          out[n] = typeof c === 'string' ? {} : c;
        }
      }
    } catch {}
    return out;
  }, [fingerprintState]);

  // Convert builderBindings to RoleState format
  const roleState: RoleState = useMemo(() => {
    const xArr = Array.isArray(builderBindings.x) ? builderBindings.x.filter(Boolean) : [];
    const yArr = Array.isArray(builderBindings.y) ? builderBindings.y.filter(Boolean) : [];
    
    return {
      x: xArr.length > 0 ? xArr[0] : null,
      y: yArr.length === 0 ? null : (yArr.length === 1 ? yArr[0] : yArr),
      series: builderBindings.series || null,
      facet: builderBindings.facet || null,
      uncertainty: builderBindings.uncertainty || null,
      n: builderBindings.n || null,
    };
  }, [builderBindings]);

  // Get recommendations based on current roles
  const recommendations = useChartRecommendations(fingerprint, roleState);

  // Check if fingerprint is ready (has columns)
  const fingerprintReady = useMemo(() => {
    return !!(fingerprintState?.columns && Array.isArray(fingerprintState.columns) && fingerprintState.columns.length > 0);
  }, [fingerprintState]);

  // fetchLatestTakehome removed (unused)
  const renderBuilder = () => {
    if (!showBuilder) {
      return (
        <div className="my-2 p-2 border rounded-md bg-background w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <div className="text-sm font-medium text-foreground">Chart Builder</div>
              <TooltipProvider>
                <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      aria-label="More information about Chart Builder"
                    >
                      <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="right" 
                    sideOffset={10}
                    className="max-w-[340px]"
                    aria-describedby="chart-builder-info"
                  >
                    <div className="space-y-2.5 leading-relaxed" onPointerDown={(e) => e.stopPropagation()}>
                      <div className="font-medium text-xs leading-tight">Prefer typing instead?</div>
                      <p className="text-[11px] text-muted-foreground leading-[1.5]">
                        Chart Builder is <span className="font-semibold text-foreground">optional</span>. Use it to help build your request, or skip it and just <span className="font-semibold text-foreground">type what you want</span> in the chat below.
                      </p>
                      <p className="text-[10px] text-muted-foreground/80 leading-[1.5]">
                        More chart types:{' '}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSeeAllCharts(true);
                            setTooltipOpen(false);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground bg-muted/50 hover:bg-muted hover:text-foreground border border-border/50 rounded cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 transition-colors"
                        >
                          Show all
                        </button>
                        .
                      </p>
                      <div className="pt-0.5 space-y-1">
                        <p className="text-[10px] font-medium text-muted-foreground">Try:</p>
                        <div className="text-[10px] text-muted-foreground/70 leading-[1.5] space-y-1">
                          <div>&quot;Week 12: baseline risk vs biomarker change, by treatment group, add a trend line and correlation.&quot;</div>
                          <div>&quot;Bar chart of average Y by category with N and error bars. Run ANOVA and show the p-value in the subtitle.&quot;</div>
                        </div>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowBuilder(true)}>Expand</Button>
          </div>
        </div>
      );
    }
    return (
      <div className="my-2 p-2 border rounded-md bg-background w-full">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-medium text-foreground">Chart Builder</div>
            <TooltipProvider>
              <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    aria-label="More information about Chart Builder"
                  >
                    <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent 
                  side="right" 
                  sideOffset={10}
                  className="max-w-[340px]"
                  aria-describedby="chart-builder-info"
                >
                  <div className="space-y-2.5 leading-relaxed" onPointerDown={(e) => e.stopPropagation()}>
                    <div className="font-medium text-xs leading-tight">Prefer typing instead?</div>
                    <p className="text-[11px] text-muted-foreground leading-[1.5]">
                      Chart Builder is <span className="font-semibold text-foreground">optional</span>. Use it to help build your request, or skip it and just <span className="font-semibold text-foreground">type what you want</span> in the chat below.
                    </p>
                    <p className="text-[10px] text-muted-foreground/80 leading-[1.5]">
                      More chart types: click{' '}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSeeAllCharts(true);
                          setTooltipOpen(false);
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        className="font-semibold text-muted-foreground hover:text-foreground underline underline-offset-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded"
                      >
                        Show all
                      </button>
                      .
                    </p>
                    <div className="pt-0.5 space-y-1">
                      <p className="text-[10px] font-medium text-muted-foreground">Try:</p>
                      <div className="text-[10px] text-muted-foreground/70 leading-[1.5] space-y-1">
                        <div>&quot;Week 12: baseline risk vs biomarker change, by treatment group, add a trend line and correlation.&quot;</div>
                        <div>&quot;Bar chart of average Y by category with N and error bars. Run ANOVA and show the p-value in the subtitle.&quot;</div>
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowBuilder(false)} aria-label="Collapse Chart Builder">
            <ChevronUp className="h-3.5 w-3.5" />
          </Button>
        </div>
      {/* Roles panel FIRST (roles-first layout) */}
      {/* Key ChartBuilderCard by fingerprint to remount when dataset changes, preventing state reuse */}
      <div className="mt-1" key={dataFingerprintId || 'no-dataset'}>
        <ChartBuilderCard
          chartId={(() => {
            try {
              if (!pickedChartId) return '';
              const p = (allCharts as any[]).find((c) => String(c.id) === String(pickedChartId));
              return String(p?.name || pickedChartId || '');
            } catch { return String(pickedChartId || ''); }
          })()}
          columns={(() => {
            try {
              const cols = fingerprintState?.columns;
              return Array.isArray(cols) ? cols.map((c: any) => ({ name: typeof c === 'string' ? c : (c.name || c.key || '') })) : [];
            } catch { return []; }
          })()}
          rolesSpec={{ x: { multiple: true }, y: { multiple: true }, series: { multiple: false }, facet: { multiple: false } } as any}
          columnMetaByName={columnMetaByName}
          tourHighlightActive={tourState.isActive && (tourState.currentStep === 1 || tourState.currentStep === 2)}
          fingerprintReady={fingerprintReady}
        onBindingsChange={(b, text) => {
          setBuilderBindings(b);
          setInputValue((prev) => {
            // Skip exactly one builder update immediately after sending, then allow updates again
            if (clearedBySendRef.current) {
              clearedBySendRef.current = false;
              lastBuilderTextRef.current = text;
              return prev;
            }
            
            // If builder text hasn't changed, don't update (prevents overwriting user input)
            if (text === lastBuilderTextRef.current) {
              return prev;
            }
            
            // Dataset-aware guard: only auto-inject if user hasn't manually edited/cleared input for this dataset
            const currentFpId = dataFingerprintId || '';
            const userHasTouched = currentFpId ? userTouchedInputRef.current.get(currentFpId) : false;
            
            // Update if input is empty AND user hasn't touched it for this dataset
            if ((!prev || prev.trim() === '') && !userHasTouched) {
              lastBuilderTextRef.current = text;
              return text;
            }
            
            // If user has cleared input, don't reinject (prevents ghost text bug)
            if ((!prev || prev.trim() === '') && userHasTouched) {
              return prev; // Keep it empty
            }
            
            // Update if input exactly matches the last builder-provided text AND user hasn't touched it
            if (prev === lastBuilderTextRef.current && !userHasTouched) {
              lastBuilderTextRef.current = text;
              return text;
            }
            
            // Compute base prompt once (chart name without bindings)
            const chartName = pickedChartId ? (() => {
              try {
                const p = (allCharts as any[]).find((c) => String(c.id) === String(pickedChartId));
                return String(p?.name || pickedChartId || '');
              } catch { return String(pickedChartId || ''); }
            })() : null;
            const basePrompt = buildPrompt(chartName, {});
            
            // Update if input matches the base chart prompt (without bindings)
            // This handles the case where user picks a chart and then selects roles
            if (prev.trim() === basePrompt.trim()) {
              lastBuilderTextRef.current = text;
              return text;
            }
            
            // If there's existing text, append or update the builder's role bindings
            // Extract the extras part (role bindings) from the builder prompt
            const extras = text.replace(basePrompt, '').trim();
            if (extras) {
              const prevTrimmed = prev.trim();
              const prevLower = prev.toLowerCase();
              const extrasLower = extras.toLowerCase();
              
              // Check if input already ends with these exact extras (already up-to-date)
              const endsWithExtras = prevLower.endsWith(extrasLower);
              
              if (endsWithExtras) {
                // Extras are already at the end and match, no update needed
                lastBuilderTextRef.current = text;
                return prev;
              }
              
              // Check if there are existing extras in the text that need to be replaced
              // Find where extras start (look for "with X =", "and Y =", "color by", etc.)
              const extrasStartPattern = /(?:,\s*)?(with\s+x\s*=|and\s+y\s*=|color\s+by|facet\s+by|uncertainty\s*=|n\s+labels:)/i;
              const extrasStartMatch = prevTrimmed.match(extrasStartPattern);
              
              if (extrasStartMatch && extrasStartMatch.index !== undefined) {
                // Found existing extras, replace everything from that point onwards
                const beforeExtras = prevTrimmed.substring(0, extrasStartMatch.index).trim();
                const separator = beforeExtras.endsWith('.') ? ' ' : ', ';
                const newText = beforeExtras + separator + extras;
                lastBuilderTextRef.current = text;
                return newText;
              }
              
              // No existing extras found, append the new extras
              const separator = prevTrimmed.endsWith('.') ? ' ' : ', ';
              const newText = prevTrimmed + separator + extras;
              lastBuilderTextRef.current = text;
              return newText;
            }
            
            // Don't update lastBuilderTextRef if we didn't change the input
            // This prevents overwriting user's manual edits on subsequent calls
            return prev;
          });
        }}
          onSend={(_text, b) => {
            try {
              const picked = Object.entries(b || {}).filter(([_, v]) => Array.isArray(v) ? v.length > 0 : !!v).map(([key]) => key);
              trackClient('builder_send_clicked', { chart: pickedChartId, picked });
            } catch {}
            // Keep builder open during runs
            handleSendMessage({ preventDefault: () => {} } as any);
          }}
        />
      </div>
      {/* Chart recommendations SECOND */}
      <div className="mt-3">
        <ChartTypeShelves
          charts={allCharts as any}
          onPickChart={(id) => {
            setPickedChartId(id);
            const picked = (allCharts as any[]).find((c) => String(c.id) === String(id));
            const displayName = picked?.name || String(id);
            const prompt = buildPrompt(displayName, {});
            setInputValue(prompt);
            lastBuilderTextRef.current = prompt;
            setBuilderOpen(true);
          }}
          stage={recommendations.stage}
          recommendedIds={recommendations.recommendedIds}
          hint={recommendations.hint}
          onShowAll={() => setSeeAllCharts(true)}
        />
      </div>
      <ChartTypeGridDrawer
        open={seeAllCharts}
        onOpenChange={setSeeAllCharts}
        charts={allCharts as any}
        onPick={(id) => {
          setPickedChartId(id);
          const picked = (allCharts as any[]).find((c) => String(c.id) === String(id));
          const displayName = picked?.name || String(id);
          const prompt = buildPrompt(displayName, {});
          setInputValue(prompt);
          lastBuilderTextRef.current = prompt;
          setBuilderOpen(true);
        }}
      />
      </div>
    );
  };

  // Manual injection hook for debugging
  useEffect(() => {
    console.log('[takehome] listener attach');
    const onInject = (e: any) => {
      try {
        const th = e?.detail;
        console.log('[takehome] inject event', th && (th.markdown ? th.markdown.length : 0));
        if (!th?.markdown) return;
        setMessages(prev => [...prev, { id: Date.now().toString() + '_takehome_manual', role: 'assistant', content: th.markdown, timestamp: new Date(), asMarkdown: true }]);
      } catch {}
    };
    window.addEventListener('lumi:takehome' as any, onInject as any);
    return () => window.removeEventListener('lumi:takehome' as any, onInject as any);
  }, []);

  // Auto-scroll behavior with one-time exception for first Data Summary appearance
  useEffect(() => {
    const tryScrollToSummaryIfNeeded = () => {
      const nextId = nextFingerprintToScrollRef.current;
      if (!nextId) return false;
      // If already scrolled for this fingerprint, skip
      if (scrolledFingerprintIdsRef.current.has(nextId)) {
        nextFingerprintToScrollRef.current = null;
        return false;
      }
      // Attempt to scroll the summary container to the top
      const node = summaryContainerRef.current;
      if (node && scrollRef.current) {
        // Align the summary container with the top of the scroll area
        try {
          node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch {
          // Fallback: set parent scrollTop to the container offset
          try {
            const parent = scrollRef.current;
            const top = (node as any).offsetTop ?? 0;
            parent.scrollTop = top;
          } catch {}
        }
        scrolledFingerprintIdsRef.current.add(nextId);
        nextFingerprintToScrollRef.current = null;
        return true;
      }
      return false;
    };

    const scrollToBottom = () => {
      // If we just inserted summary and chose to scroll to it, skip bottom scroll once
      if (skipNextAutoScrollRef.current) {
        skipNextAutoScrollRef.current = false;
        return;
      }
      // Try scrolling using the messages end ref first (more reliable)
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (scrollRef.current) {
        // Fallback to manual scroll
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }
        });
      }
    };

    // Try to align summary to top first; if it succeeds, skip bottom scroll once
    const didSummaryScroll = tryScrollToSummaryIfNeeded();
    if (didSummaryScroll) {
      skipNextAutoScrollRef.current = true;
    } else {
      // Otherwise, default behavior: scroll to bottom immediately and after a short delay
      scrollToBottom();
      const timeoutId = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [messages, isProcessing, isTyping, progressMessage, progressLabel]);

  // Removed automatic fingerprint fetch on files-change to avoid unintended dataset selection

  const fetchFingerprint = async (fileId: string) => {
    try {
      const response = await apiRequest('GET', `/api/ai/v2/fingerprint/${fileId}`);
      const result = await response.json();
      if (result.success) {
        // Inject placeholders first to avoid fallback flashes
        try { ensureSummaryPlaceholder(String(result.fingerprintId), result.fingerprint || {}); } catch {}
        setDataFingerprintId(result.fingerprintId);
        console.log('Data fingerprint loaded:', result.fingerprintId);
      }
    } catch (error) {
      console.log('No fingerprint yet for file:', fileId);
    }
  };

  // Listen to cross-component data analysis events (from Data tab uploads)
  useEffect(() => {
    const onAnalyzed = (e: any) => {
      console.log('Lumi received lumi:dataAnalyzed event:', e);
      const d = e?.detail || {};
      if (!d) {
        console.log('❌ No detail in event');
        return;
      }
      console.log('Event detail data:', d);
      
      if (d.fingerprintId) {
        console.log('🔑 Setting fingerprint ID:', d.fingerprintId);
        // Ensure placeholders exist BEFORE setting the fingerprint id
        try { ensureSummaryPlaceholder(String(d.fingerprintId), d.fingerprint || {}); } catch {}
        setDataFingerprintId(d.fingerprintId);
        try {
          const pid = String(currentProject?.id || '');
          const cid = String(routeDraftId || (currentPlot as any)?.id || '');
          if (pid && cid) setLiveDataFingerprint(pid, cid, String(d.fingerprintId), { fileId: d.fileId });
        } catch {}
        // Prefer explicit fileId; do not fallback to first file
        const idForFetch: any = d.fileId || null;
        if (idForFetch) {
          try { fetchFingerprint(idForFetch); } catch {}
        } else if (d.fingerprint) {
          try { ensureSummaryPlaceholder(String(d.fingerprintId), d.fingerprint); } catch {}
        }
      }
      // Do not add the old verbose text message; the summary card will be rendered
    };
    window.addEventListener('lumi:dataAnalyzed' as any, onAnalyzed as any);
    return () => window.removeEventListener('lumi:dataAnalyzed' as any, onAnalyzed as any);
  }, []);

  // Helper to materialize and run from a planning brief (Option 2/3 or Modify)
  async function materializeAndRunFromBrief(brief: any): Promise<boolean> {
    if (!currentProject?.id || !user?.id) return false;
    if (!routeDraftId) {
      const bound = await ensureBoundDraft();
      if (!bound) return false;
    }
    try {
      // Proactively ensure a draft claim before running
      if (!claimTokenRef.current || !boundDraftIdRef.current) {
        try { await ensureBoundDraft(); } catch {}
      }
      const ref = getDatasetRef();
      const selectedChartIdCanonical = (() => {
        try {
          const picked = (allCharts as any[]).find((c) => String(c.id) === String(pickedChartId));
          return picked?.id || (pickedChartId || undefined);
        } catch { return pickedChartId || undefined; }
      })();
      const st0 = usePlotsStore.getState();
      const ctxFp0 = st0.getLiveDataFingerprint?.(String(currentProject.id), String(boundDraftIdRef.current || routeDraftId || '')) || null;
      const curFp0 = (st0.currentPlot as any)?.latestDataFingerprintId || null;
      const dsId0 = curFp0 || ctxFp0 || dataFingerprintId || null;
      const execPlotId0 = String(
        boundDraftIdRef.current ||
        routeDraftId ||
        (await ensureBoundDraft())?.draftId
      );
      const payloadBase0 = {
        planId: execPlotId0,            // draft/plot id
        planning_brief: brief,          // keep the brief (may reference plan_*)
        sourcePlanId: (brief as any)?.from_plan_id, // optional breadcrumb
        ...(dsId0 ? { dataFingerprintId: dsId0 } : {}),
        datasetRef: ref,
        userId: user.id,
        projectId: currentProject.id,
        confirmed: true,
        ...(selectedChartIdCanonical ? { selectedChartId: selectedChartIdCanonical } : {}),
      } as any;
      try {
        const dsHash = await hashWeb(String(dsId0 || ''));
        // Count dataset stats from last fingerprint
        const fp: any = (window as any).__last_fp_for_chat || (typeof dsId0 === 'string' ? ((window as any).demoFingerprints?.[dsId0] || null) : null);
        const preview = Array.isArray(fp?.preview) ? fp.preview : (Array.isArray(fp?.data) ? fp.data : []);
        const dataset_cols = Array.isArray(fp?.columns) ? fp.columns.length : (preview[0] ? Object.keys(preview[0]).length : 0);
        // Count planned transforms (best-effort)
        const transforms_hint = (() => {
          try {
            const rec = (brief as any)?.recommended || {};
            const arrs = [rec?.transforms, rec?.filters, rec?.stats, rec?.encodings?.hover].filter(Array.isArray);
            return arrs.reduce((n: number, a: any[]) => n + a.length, 0);
          } catch { return 0; }
        })();
        // Record run start for duration measurement
        runStartMsRef.current.set(String(execPlotId0), performance.now());
        track('plot:attempted', {
          dataset_id_hash: dsHash,
          prompt_len: Number(String((brief as any)?.prior_prompt || '').length || 0),
          transforms_hint,
          columns_selected: Number(dataset_cols || 0),
          credits_before: Number(credits?.remaining ?? -1),
        } as any);
      } catch {}
      const pendingRunId0 = (window as any).__lumiPendingRunId || undefined;
      let resp = await apiRequest('POST', '/api/ai/v2/execute-plan', { ...payloadBase0, ...(pendingRunId0 ? { run_id: pendingRunId0 } : {}), ...buildDraftClaim() });
      let respJson0: any | undefined = undefined;
      // Cold start handling: retry once on retryable TIMEOUT_SPAWN (504)
      if (resp.status === 504) {
        try {
          respJson0 = await resp.json().catch(() => undefined);
          if (respJson0 && respJson0.retryable === true) {
            toast({ title: 'Warming compute', description: 'Cold start detected. Retrying now…' });
            await new Promise((r) => setTimeout(r, 1500));
            resp = await apiRequest('POST', '/api/ai/v2/execute-plan', { ...payloadBase0, ...buildDraftClaim() });
            respJson0 = undefined; // fresh response; clear cached body
          }
        } catch {}
      }
      if (resp.status === 409) {
        try { await ensureBoundDraft(); } catch {}
        const pendingRunIdRetry0 = (window as any).__lumiPendingRunId || undefined;
        const retryPayload0 = { ...payloadBase0, ...(pendingRunIdRetry0 ? { run_id: pendingRunIdRetry0 } : {}), ...buildDraftClaim() } as any;
        resp = await apiRequest('POST', '/api/ai/v2/execute-plan', retryPayload0);
      }
      try { if ((import.meta as any).env?.MODE !== 'production') console.debug('exec_body_dataset', { projectId: currentProject.id, draftId: (boundDraftIdRef.current || routeDraftId), datasetId: dsId0 }); } catch {}
      if (resp.status === 402) return false;
      if (resp && (resp as any).ok) {
        setIsProcessing(true);
        // Defer stage transitions to websocket progress events
      }
      const result = respJson0 ?? (await resp.json());
      // Handle structured API error (no network error)
      if (handleStructuredApiError(result)) return false;
      if (result?.error) return false;
      // Persist draftId for Data tab and Save flow; immediately switch selection to draftId
      try {
        if (result?.draftId && currentProject?.id) {
          (window as any).__lumiplotLastDraftId = String(result.draftId);
          localStorage.setItem(`lumiplot:project:${currentProject.id}:lastDraftId`, String(result.draftId));
          // Switch current plot selection to the draftId immediately to avoid stale latest.json fetches
          try {
            const oldId = (window as any).__lumiplotCurrentPlotId || null;
            try { if (oldId) { await queryClient.cancelQueries({ queryKey: scopedKey([user?.id, '/api/ai/v2/plots', oldId, 'versions', 'latest']) as any }); } } catch {}
            const newPlot = {
              id: String(result.draftId),
              projectId: currentProject.id,
              title: 'Draft (in progress)',
              plotlyJson: result.plotlyJson || { data: [], layout: {} },
              pythonCode: result.pythonCode || null,
              createdAt: new Date(),
              creditsConsumed: 0,
            } as any;
            setCurrentPlot(newPlot);
            try { setCurrentPlotArtifacts({ id: String(result.draftId), projectId: String(currentProject.id), plotlyJson: newPlot.plotlyJson, artifactSource: 'draft' }); } catch {}
            try { (window as any).__lumiplotCurrentPlotId = String(result.draftId); } catch {}
            // Fire code export if HTTP materialization returned a figure
            try { if (currentProject?.id && result?.plotlyJson) { void triggerExportCode(String(currentProject.id), String(result.draftId), result.plotlyJson); } } catch {}
            // Immediately subscribe to the draft room so upcoming WS events are not dropped
            try {
              const pid = String(currentProject.id);
              const did = String(result.draftId);
              const { subscribeProject } = await import('@/ws/socket');
              const ack = await subscribeProject(pid, did);
              if (ack !== 'ok') { console.warn('[ws] subscribe_project draft ack!=ok', { pid, did }); }
            } catch {}
          } catch {}
        }
      } catch {}
      return true;
    } catch (e) {
      // Handle network/connection errors that throw before response
      showRunErrorToast(e);
      return false;
    }
  }

  function showRunErrorToast(err: unknown) {
    try {
      const raw = String((err as any)?.message || err || '');
      const msg = raw.toUpperCase();
      const isModalDown = msg.includes('RETRY_FAILED_MODAL_ONLY') || msg.includes('MODAL_RUN_FAILED');
      const isTransport = msg.includes('TRANSPORT_ERROR') || msg.includes('SANDBOX_TRANSPORT_ERROR');
      const isEmpty = msg.includes('EMPTY_FIGURE_AFTER_RETRY') || msg.includes('EMPTY_FIGURE');
      const isStorageError = msg.includes('STORAGE_CONNECTION_ERROR') || msg.includes('STORAGE_ERROR') || 
                           msg.includes('STORAGEUNKNOWNERROR') || msg.includes('FETCH FAILED') ||
                           msg.includes('FAILED TO CONNECT TO STORAGE') || msg.includes('CONNECT TIMEOUT');
      const isNetworkError = msg.includes('FETCH FAILED') || msg.includes('NETWORK ERROR') || 
                           msg.includes('TIMEOUT') || msg.includes('CONNECTION') ||
                           (msg.includes('500:') && (msg.includes('FETCH') || msg.includes('TIMEOUT')));
      
      if (isStorageError || isNetworkError) {
        toast({
          title: 'Connection error',
          description: 'Failed to connect to storage. Please try again.',
          variant: 'destructive'
        });
        return;
      }
      if (isModalDown || isTransport) {
        toast({
          title: 'Service is temporarily unavailable',
          description: 'Our analysis engine is down right now. No credits were used. Please try again in a minute.',
          variant: 'destructive'
        });
        return;
      }
      if (isEmpty) {
        toast({
          title: 'No chart could be generated',
          description: 'We could not find enough usable data. No credits were used. Please try tweaking your prompt or filter. We are constantly improving Lumi\'s capacity to plot. If the issue persists, please let us know to help us improve.',
        });
        return;
      }
    } catch {}
    toast({ title: "Run didn't complete", description: 'Open Logs for details, then try again in a moment.' });
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    // Gate: prevent double submission while processing
    if (isProcessing || execInFlightRef.current) {
      console.warn('[handleSendMessage] Blocked duplicate call - already processing');
      return;
    }
    // Set guard flag IMMEDIATELY after check to prevent race condition
    // This must be set synchronously before any async operations or early returns
    execInFlightRef.current = true;
    
    if (!inputValue.trim() || !currentProject) {
      execInFlightRef.current = false; // Reset if early return
      return;
    }
    const ref = getDatasetRef();
    // Prevent sending when no dataset is selected; server requires dataset_id or dataset_path
    if (!ref && !dataFingerprintId) {
      execInFlightRef.current = false; // Reset if early return
      toast({ title: 'Dataset required', description: 'Upload a CSV or choose an existing file to continue.', variant: 'destructive' });
      setShowDataModal(true);
      return;
    }
    
    // Check credits BEFORE setting loading state - if insufficient, show billing modal instead
    if (insufficient) {
      execInFlightRef.current = false; // Reset if early return
      setBillingOpen(true);
      return;
    }
    
    // Check dirty state: if current draft has unsaved edits, show confirmation dialog
    if (isDirty && routeDraftId) {
      execInFlightRef.current = false; // Reset guard
      setIsProcessing(false);
      // Store the send function to call after user confirms
      pendingSendRef.current = async () => {
        execInFlightRef.current = true;
        setIsProcessing(true);
        setShowSendSpinner(true);
        sendSpinnerStartRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        await doSendMessage();
      };
      setShowDirtyConfirm(true);
      return;
    }
    
    // Set loading state IMMEDIATELY after validation checks to show UI feedback right away
    // This must happen before any async operations to eliminate perceived lag
    setIsProcessing(true);
    setShowSendSpinner(true);
    sendSpinnerStartRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    
    await doSendMessage();
  };

  const doSendMessage = async () => {
    // Always ensure a fresh draft when current draft is clean (one request = one new plot)
    // If we have a routeDraftId but it's clean, create a new one
    const shouldCreateNew = !isDirty && !!routeDraftId;
    
    if (!routeDraftId || shouldCreateNew) {
      const result = await claimDraftId(shouldCreateNew);
      if (!result) {
        execInFlightRef.current = false; // Reset if early return
        setIsProcessing(false);
        stopSendSpinner();
        toast({ title: 'Could not start a new plot', description: 'Please try again, or reload the page if the issue persists.', variant: 'destructive' });
        return;
      }
      // Bind to route without resetting chat history
      await bindDraftToRoute(result.pid, result.sid, result.claimToken, { resetChat: false });
    }
    if (askUserQuestion) {
      // Clear clarification state once user responds
      setAskUserQuestion(null);
    }

    // Starting a new run: clear terminal + prior run id
    resetRunState('send');

    // Reset textarea height after sending
    const textarea = document.querySelector('[data-testid="input-chat"]') as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = '40px';
    }

    // If we are in Modify mode, use this user message as modify_text and run
    if (pendingModify) {
      // Set processing state to prevent duplicate calls
      setIsProcessing(true);
      execInFlightRef.current = true;
      const userText = inputValue.trim();
      setInputValue('');
      // Keep builder open during runs
      setMessages((prev) => [
        ...prev,
        { id: Date.now().toString(), role: 'user', content: userText, timestamp: new Date() },
      ]);
      const lastUser = [...messages].reverse().find((m) => m.role === 'user')?.content || '';
      const brief = {
        type: 'modify',
        from_plan_id: pendingModify.planId,
        base_option_label: pendingModify.baseLabel,
        prior_prompt: lastUser,
        modify_text: userText,
        base_plan_hint: {
          chart_family: pendingModify.basePlan?.recommended?.chart_family || pendingModify.basePlan?.chart_family,
          encodings: pendingModify.basePlan?.recommended?.encodings || pendingModify.basePlan?.encodings,
        },
      };
      setAskUserQuestion(null);
      setPendingModify(null);
      // Spinner already set earlier, no need to set again
      await materializeAndRunFromBrief(brief);
      stopSendSpinner();
      return;
    }

    // Processing state already set earlier, no need to set again
    
    // Capture inputValue before clearing (needed for retry logic)
    const messageText = inputValue.trim();
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    // Persist user message (best-effort)
    try { void apiRequest('POST', `/api/projects/${currentProject.id}/messages`, { role: 'user', content: messageText }); } catch {}
    // Dispatch event for tour manager to advance from send step
    window.dispatchEvent(new CustomEvent('lumi:message-sent', { detail: { messageText } }));
    // Clear composer immediately so text isn't duplicated below the chart
    setInputValue('');
    // Prevent immediate ghost prompt repopulation right after send
    clearedBySendRef.current = true;
    // Keep builder open during runs; user can collapse manually if desired
    // Spinner already started earlier, just set the loading stage
    setLoadingStage('recipe');
    
    // Ensure scroll to bottom after user message unless we plan to show summary at top
    setTimeout(() => {
      if (skipNextAutoScrollRef.current) return;
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 50);

    try {
      console.log('Sending message to Lumi V2:', {
        message: messageText,
        hasData: !!dataFingerprintId,
        project: currentProject.id
      });

      // Build sliding chat history (last 12 turns)
      const history = messages
        .slice(-12)
        .map((m) => ({ role: m.role, content: m.content, ts: (m.timestamp || new Date()).toISOString?.() || new Date().toISOString(), envelope: (m as any)?.envelope || undefined }));

      // Single API call - Lumi handles everything
      const selectedChartIdCanonical2 = (() => {
        try {
          const picked = (allCharts as any[]).find((c) => String(c.id) === String(pickedChartId));
          return picked?.id || (pickedChartId || undefined);
        } catch { return pickedChartId || undefined; }
      })();
      const response = await apiRequest('POST', '/api/ai/v2/process-message', {
        action: 'message',
        message: messageText,
        dataFingerprintId: dataFingerprintId,
        requireApproval: true,
        planningOnly: true,
        currentPlotId: (boundDraftIdRef.current || routeDraftId || null),
        userId: user?.id || 'demo-user-id',
        projectId: currentProject.id,
        chat_history: history,
        ...(claimTokenRef.current ? { claimToken: claimTokenRef.current } : {}),
        ...(selectedChartIdCanonical2 ? { selectedChartId: selectedChartIdCanonical2 } : {}),
      });

      const result = await response.json();
      console.log('Lumi V2 response:', result);

      // Add Lumi's response
      const assistantMessage: ChatMessage = {
        id: Date.now().toString() + '_assistant',
        role: 'assistant',
        content: result.message,
        timestamp: new Date(),
        intent: result.intent,
        hasPlot: !!result.plotlyJson,
        plan: result.plan ? { ...result.plan, options_ranked: (result.options_ranked || result.plan?.options_ranked || []) } : undefined,
        planId: undefined as any, // do not treat synthetic plan_* as executable id
        requiresApproval: result.requiresApproval,
        envelope: (result as any)?.envelope || undefined
      };
      setMessages(prev => [...prev, assistantMessage]);
      // Do NOT clear the stage on initial orchestration response; keep progress visible until WS events end it
      // Persist assistant system response (best-effort)
      try { void apiRequest('POST', `/api/projects/${currentProject.id}/messages`, { role: 'assistant', content: result.message || '' }); } catch {}

      // Clarification flow: orchestrator asks a short follow-up question
      if (String(result.intent || '').toLowerCase() === 'ask_user') {
        setIsProcessing(false);
        execInFlightRef.current = false;
        setLoadingStage(null);
        stopSendSpinner();
        setAskUserQuestion(result.message || 'Could you clarify your request?');
        // Persist the clarification question as an assistant message for history continuity
        try { void apiRequest('POST', `/api/projects/${currentProject.id}/messages`, { role: 'assistant', content: result.message || '' }); } catch {}
        return;
      }

      // IMPORTANT: planning-only path returns a plan and stops; no WS terminal events will arrive.
      if ((result.requiresApproval && result.plan) || (result.requiresData && !dataFingerprintId)) {
        // End the faux "run" so UI isn't stuck.
        markTerminal();
        stopSendSpinner();
      }

      // If plan requires approval, show approval buttons
      if (result.requiresApproval && result.plan) {
        console.log('Plan requires approval:', result.plan);
        // User must manually approve all plans - no auto-approval
        // 🔑 Tell TourManager that the plan UI is (about to be) ready
        // Dispatch after React renders the plan card (requestAnimationFrame waits for next paint)
        requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('lumiplot-plan-ready'));
        });
      }
      
      // Enforce manual approval: ignore any legacy plot payloads from process-message
      if (result.plotlyJson) {
        console.warn('[process-message] Ignoring plotlyJson to prevent auto-run (manual approval required).');
      }

      // If data is required but missing, show upload prompt
      if (result.requiresData && !dataFingerprintId) {
        setShowUpload(true);
      }

    } catch (error) {
      console.error('❌ Error processing message:', error);
      const text = String((error as any)?.message || '').trim();
      // Check for CONFLICT_TOKEN error - can be in format "409: {"error":"CONFLICT_TOKEN"}" or just "CONFLICT_TOKEN"
      const isConflictToken = text.includes('409') && (text.includes('CONFLICT_TOKEN') || text.includes('CONFLICT'));
      console.log('🔍 Error analysis:', { text, isConflictToken });
      
      // Handle CONFLICT_TOKEN error: refresh claim token and retry
      if (isConflictToken) {
        console.log('🔄 CONFLICT_TOKEN detected, refreshing claim token and retrying...');
        try {
          // Reconstruct user message object since React state updates are async
          // The user message was added to state but might not be in messages yet
          const userMsg: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: messageText,
            timestamp: new Date(),
          };
          console.log('💾 Preserving user message:', { messageText, userMsgId: userMsg.id });
          
          // Refresh the claim token first (this may reset some state)
          console.log('🔄 Calling ensureBoundDraft()...');
          const refreshed = await ensureBoundDraft();
          console.log('🔄 ensureBoundDraft() returned:', refreshed);
          if (refreshed && refreshed.draftId && refreshed.claimToken) {
            // Immediately restore user message if it was cleared by resetForNewDraft
            // Use the captured userMessage object to ensure we have the exact message
            if (userMsg) {
              // Use a function update to ensure we're working with the latest state
              setMessages(prev => {
                // Check if user message already exists (by content, not just ID)
                const hasUserMsg = prev.some(m => m.role === 'user' && m.content === messageText);
                if (hasUserMsg) {
                  console.log('✅ User message already present in messages');
                  return prev;
                }
                // Add user message back - preserve it even if resetForNewDraft cleared it
                console.log('🔄 Restoring user message to chat');
                return [...prev, userMsg];
              });
            }
            
            // Set loading indicators AFTER ensureBoundDraft (which may have cleared them)
            setLoadingStage('recipe');
            setShowSendSpinner(true);
            sendSpinnerStartRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
            setIsProcessing(true);
            
            console.log('✅ Claim token refreshed:', { draftId: refreshed.draftId, hasToken: !!refreshed.claimToken });
            // Retry the request with the new token and draft ID
            const retryHistory = messages
              .slice(-12)
              .map((m) => ({ role: m.role, content: m.content, ts: (m.timestamp || new Date()).toISOString?.() || new Date().toISOString(), envelope: (m as any)?.envelope || undefined }));
            
            const selectedChartIdCanonical2 = (() => {
              try {
                const picked = (allCharts as any[]).find((c) => String(c.id) === String(pickedChartId));
                return picked?.id || (pickedChartId || undefined);
              } catch { return pickedChartId || undefined; }
            })();
            
            console.log('🔄 Retrying request with refreshed token...', {
              draftId: refreshed.draftId,
              hasClaimToken: !!refreshed.claimToken,
              messageLength: messageText.length
            });
            
            try {
              const retryResponse = await apiRequest('POST', '/api/ai/v2/process-message', {
                action: 'message',
                message: messageText,
                dataFingerprintId: dataFingerprintId,
                requireApproval: true,
                planningOnly: true,
                currentPlotId: refreshed.draftId, // Use the refreshed draft ID
                userId: user?.id || 'demo-user-id',
                projectId: currentProject.id,
                chat_history: retryHistory,
                claimToken: refreshed.claimToken, // Use the refreshed claim token
                ...(selectedChartIdCanonical2 ? { selectedChartId: selectedChartIdCanonical2 } : {}),
              });
              
              console.log('✅ Retry response received:', { status: retryResponse.status, ok: retryResponse.ok });
              const retryResult = await retryResponse.json();
              console.log('✅ Lumi V2 retry response:', retryResult);
              
              // Add Lumi's response
              const assistantMessage: ChatMessage = {
                id: Date.now().toString() + '_assistant',
                role: 'assistant',
                content: retryResult.message,
                timestamp: new Date(),
                intent: retryResult.intent,
                hasPlot: !!retryResult.plotlyJson,
                plan: retryResult.plan ? { ...retryResult.plan, options_ranked: (retryResult.options_ranked || retryResult.plan?.options_ranked || []) } : undefined,
                planId: undefined as any,
                requiresApproval: retryResult.requiresApproval,
                envelope: (retryResult as any)?.envelope || undefined
              };
              setMessages(prev => [...prev, assistantMessage]);
              try { void apiRequest('POST', `/api/projects/${currentProject.id}/messages`, { role: 'assistant', content: retryResult.message || '' }); } catch {}
              
              // Handle clarification flow
              if (String(retryResult.intent || '').toLowerCase() === 'ask_user') {
                // Ensure loading stage is visible for a minimum time before clearing
                const minDisplayTime = 500; // 500ms minimum
                const elapsed = sendSpinnerStartRef.current ? 
                  ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - sendSpinnerStartRef.current) : 0;
                const remaining = Math.max(0, minDisplayTime - elapsed);
                
                setTimeout(() => {
                  setIsProcessing(false);
                  execInFlightRef.current = false;
                  setLoadingStage(null);
                  stopSendSpinner();
                }, remaining);
                
                setAskUserQuestion(retryResult.message || 'Could you clarify your request?');
                try { void apiRequest('POST', `/api/projects/${currentProject.id}/messages`, { role: 'assistant', content: retryResult.message || '' }); } catch {}
                return;
              }
              
              // Handle planning-only path
              if ((retryResult.requiresApproval && retryResult.plan) || (retryResult.requiresData && !dataFingerprintId)) {
                markTerminal();
                stopSendSpinner();
              }
              
              // Handle plan approval requirement
              if (retryResult.requiresApproval && retryResult.plan) {
                console.log('Plan requires approval:', retryResult.plan);
                // 🔑 Tell TourManager that the plan UI is (about to be) ready
                // Dispatch after React renders the plan card (requestAnimationFrame waits for next paint)
                requestAnimationFrame(() => {
                window.dispatchEvent(new CustomEvent('lumiplot-plan-ready'));
                });
              }
              
              if (retryResult.plotlyJson) {
                console.warn('[process-message] Ignoring plotlyJson to prevent auto-run (manual approval required).');
              }
              
              if (retryResult.requiresData && !dataFingerprintId) {
                setShowUpload(true);
              }
              
              return; // Successfully retried, exit early
            } catch (retryApiError) {
              // If retry also fails, log and rethrow to be caught by outer catch
              console.error('❌ Retry API request failed:', retryApiError);
              throw retryApiError;
            }
          } else {
            console.warn('⚠️ ensureBoundDraft() did not return valid draftId/claimToken:', refreshed);
          }
        } catch (retryError) {
          console.error('❌ Retry after CONFLICT_TOKEN refresh failed:', retryError);
          console.error('❌ Retry error details:', {
            message: (retryError as any)?.message,
            stack: (retryError as any)?.stack
          });
          // Fall through to show error message
        }
        
        // If retry failed, show user-friendly error
        setMessages(prev => [...prev, {
          id: Date.now().toString() + '_error',
          role: 'assistant',
          content: 'I had trouble connecting to your plot. Please try sending your message again.',
          timestamp: new Date(),
        }]);
        toast({
          title: 'Connection issue',
          description: 'Your plot session was refreshed. Please try again.',
          variant: 'default',
        });
      } else {
        // Other errors: show original error message
        const errorText = text || 'Sorry, Lumi has encountered an issue and we cannot render this plot. Please try tweaking your prompt or filter. We are constantly improving Lumi\'s capacity to plot. If the issue persists, please let us know to help us improve.';
        setMessages(prev => [...prev, {
          id: Date.now().toString() + '_error',
          role: 'assistant',
          content: errorText,
          timestamp: new Date(),
        }]);
      }
    } finally {
      stopSendSpinner();
    }
  };

  // Handle plan approval via guarded run; approve only on success
  const handleApprovePlan = async (messageId: string, plan: any, planId: string) => {
    // Gate: prevent double approval while processing (early warning for debugging)
    // Note: runWithGuards will also check this, but we check here for better logging
    if (isProcessing || execInFlightRef.current) {
      console.warn('[handleApprovePlan] Blocked duplicate call - already processing', { planId });
      return;
    }
    
    console.log('Approving plan:', planId);
    const ref = getDatasetRef();
    if (!ref) {
      toast({ title: 'Select a dataset to run this plan.' });
      try { trackClient('approve_click_blocked_dataset', { planId }); } catch {}
      return;
    }

    try { trackClient('approve_run_started', { planId }); } catch {}

    const res = await runWithGuards(async () => {
      resetRunState('approve');
      setIsProcessing(true);
      setStageRecipe();
      // Proactively ensure a draft claim before running
      if (!claimTokenRef.current || !boundDraftIdRef.current) {
        try { await ensureBoundDraft(); } catch {}
      }
      const selectedChartIdCanonical3 = (() => {
        try {
          const picked = (allCharts as any[]).find((c) => String(c.id) === String(pickedChartId));
          return picked?.id || (pickedChartId || undefined);
        } catch { return pickedChartId || undefined; }
      })();
      const st1 = usePlotsStore.getState();
      const ctxFp1 = st1.getLiveDataFingerprint?.(String(currentProject?.id || ''), String(boundDraftIdRef.current || routeDraftId || '')) || null;
      const curFp1 = (st1.currentPlot as any)?.latestDataFingerprintId || null;
      const dsId1 = curFp1 || ctxFp1 || dataFingerprintId || null;
      const execPlotId = String(
        boundDraftIdRef.current ||
        routeDraftId ||
        (await ensureBoundDraft())?.draftId
      );
      const payloadBase1 = {
        // Server expects planId == plot/draft id
        planId: execPlotId,
        // keep the synthetic planning id only as provenance
        sourcePlanId: planId,
        // do NOT rewrite the plan's own plan_id; keep planner's id
        plan: { ...plan, plan_id: (plan as any)?.plan_id ?? planId },
        ...(dsId1 ? { dataFingerprintId: dsId1 } : {}),
        datasetRef: ref,
        userId: user?.id || 'demo-user-id',
        projectId: currentProject?.id,
        confirmed: true,
        ...(selectedChartIdCanonical3 ? { selectedChartId: selectedChartIdCanonical3 } : {}),
      } as any;
      const pendingRunId1 = (window as any).__lumiPendingRunId || undefined;
      let response = await apiRequest('POST', '/api/ai/v2/execute-plan', { ...payloadBase1, ...(pendingRunId1 ? { run_id: pendingRunId1 } : {}), ...buildDraftClaim() });
      let respJson1: any | undefined = undefined;
      // Cold start handling: retry once on retryable TIMEOUT_SPAWN (504)
      if (response.status === 504) {
        try {
          respJson1 = await response.json().catch(() => undefined);
          if (respJson1 && respJson1.retryable === true) {
            toast({ title: 'Warming compute', description: 'Cold start detected. Retrying now…' });
            await new Promise((r) => setTimeout(r, 1500));
            response = await apiRequest('POST', '/api/ai/v2/execute-plan', { ...payloadBase1, ...buildDraftClaim() });
            respJson1 = undefined; // fresh response; clear cached body
          }
        } catch {}
      }
      if (response.status === 409) {
        try { await ensureBoundDraft(); } catch {}
        const pendingRunIdRetry1 = (window as any).__lumiPendingRunId || undefined;
        const retryPayload = { ...payloadBase1, ...(pendingRunIdRetry1 ? { run_id: pendingRunIdRetry1 } : {}), ...buildDraftClaim() } as any;
        response = await apiRequest('POST', '/api/ai/v2/execute-plan', retryPayload);
      }
      try { if ((import.meta as any).env?.MODE !== 'production') console.debug('exec_body_dataset', { projectId: currentProject?.id, draftId: (boundDraftIdRef.current || routeDraftId), datasetId: dsId1 }); } catch {}
      if (response.status === 402) {
        return false;
      }
      if (response && (response as any).ok) {
        setIsProcessing(true);
        // Let WS progress advance the chip; at most keep it in "recipe" here
        // setStageRecipe();
      }

      const result = respJson1 ?? (await response.json());
      console.log('Plot generated via HTTP:', result);
      // Handle structured API error (no network error)
      if (handleStructuredApiError(result)) return false; // stop before success-only logic

      if (result.plotlyJson) {
        // Plot arrived via HTTP; if backend won't emit WS events for this fast-path,
        // end the client-side run so the chip/button reset immediately.
        try {
          if (result?.draftId && currentProject?.id) {
            (window as any).__lumiplotLastDraftId = String(result.draftId);
            localStorage.setItem(`lumiplot:project:${currentProject.id}:lastDraftId`, String(result.draftId));
            try { const oldId = (window as any).__lumiplotCurrentPlotId || null; if (oldId) { await queryClient.cancelQueries({ queryKey: scopedKey([user?.id, '/api/ai/v2/plots', oldId, 'versions', 'latest']) as any }); } } catch {}
            const newPlot = {
              id: String(result.draftId),
              projectId: currentProject.id,
              title: 'Draft (in progress)',
              plotlyJson: result.plotlyJson || { data: [], layout: {} },
              pythonCode: result.pythonCode || null,
              createdAt: new Date(),
              creditsConsumed: 0,
            } as any;
            setCurrentPlot(newPlot);
            try { (window as any).__lumiplotCurrentPlotId = String(result.draftId); } catch {}
          }
        } catch {}

        // Best-effort: persist plot to gallery via POST /api/plots with a synthesized PlotSpec
        try {
          const pj: any = result.plotlyJson || {};
          const firstTrace: any = Array.isArray(pj.data) && pj.data.length > 0 ? pj.data[0] : {};
          const inferredType = String(firstTrace.type || 'bar');
          const plotSpec = {
            version: '1.0.0',
            plotType: inferredType,
            data: { source: '', columns: {}, filters: [], aggregations: [] },
            statistics: { tests: [], transformations: [] },
            styling: { template: 'minimal', journalPreset: 'Nature', customizations: {} },
            annotations: []
          } as any;
          await apiRequest('POST', '/api/plots', {
            projectId: currentProject?.id,
            title: `Generated Plot ${new Date().toLocaleTimeString()}`,
            plotSpec,
            plotlyJson: result.plotlyJson,
            pythonCode: result.pythonCode || null,
            creditsConsumed: result.creditsUsed || 2,
          });
        } catch (e) {
          console.warn('Plot persist skipped:', (e as any)?.message || e);
        }
        // Fire code export for HTTP fast-path
        try {
          if (currentProject?.id && result?.draftId) {
            const pid = String(currentProject.id);
            const sid = String(result?.draftId);
            void triggerExportCode(pid, sid, result.plotlyJson);
          }
        } catch {}
        markTerminal();
        return true;
      }

      if (result?.error) return false;
      return false;
    });

    if (!res.ok) {
      // Ensure UI is truthful: not approved
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, approved: false } : msg
      ));
      if (!getDatasetRef()) {
        toast({ title: 'Dataset required', description: 'Pick or upload a dataset to enable Approve & run.' });
        try { trackClient('approve_run_failed', { reason: 'dataset', planId }); } catch {}
        markTerminal();
      } else if (insufficient) {
        toast({ title: 'Insufficient credits', description: 'Please purchase more credits to continue.' });
        try { trackClient('approve_run_failed', { reason: 'credits', planId }); } catch {}
        setBillingOpen(true);
        markTerminal();
        return;
      } else {
        // Map transport/modal outages to a friendly toast
        if (res.reason === 'concurrent') {
          // Another approval is already in progress
          console.warn('[handleApprovePlan] Concurrent run detected by runWithGuards', { planId });
          toast({ title: 'Already running', description: 'Another plot is being generated. Please wait.' });
        } else if (res.reason === 'error') {
          showRunErrorToast((res as any).error);
        } else {
          // Try to parse error response if available
          try {
            // Clone response to avoid consuming it if already read
            const errorData = await (res as any).clone?.().json().catch(() => null) || 
                             await (res as any).json().catch(() => null);
            if (errorData && (errorData.error_code || errorData.error)) {
              showRunErrorToast(errorData);
            } else {
              toast({ title: 'Connection error', description: 'Failed to connect to storage. Please try again.', variant: 'destructive' });
            }
          } catch {
            toast({ title: 'Connection error', description: 'Failed to connect to storage. Please try again.', variant: 'destructive' });
          }
        }
        try { trackClient('approve_run_failed', { reason: 'api', planId }); } catch {}
        markTerminal();
      }
    } else if (res.value === true) {
      // Approve now that we know it succeeded
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, approved: true, requiresApproval: false, selectedCard: { label: 'Recommended', title: String(plan?.recommended?.title || 'Recommended'), recommended: true, plan } } : msg
      ));
      try { trackClient('approve_run_succeeded', { planId }); } catch {}
    } else {
      // Unexpected non-true value: treat as failure
      toast({ title: 'Run didn\'t complete', description: 'Open Logs for details, then try again in a moment.' });
      try { trackClient('approve_run_failed', { reason: 'api', planId }); } catch {}
      markTerminal();
    }
  };

  // Handle approving Option 2/3 brief via guarded run
  const handleApproveOptionBrief = async (messageId: string, basePlan: any, planId: string, chosenProposal: any, label: 'Option 2' | 'Option 3') => {
    const ref = getDatasetRef();
    if (!ref) {
      toast({ title: 'Select a dataset to run this plan.' });
      try { trackClient('approve_click_blocked_dataset', { planId }); } catch {}
      return;
    }
    const lastUser = [...messages].reverse().find((m) => m.role === 'user')?.content || '';
    // Get orchestrator envelope from message to preserve intent_summary (multi-turn conversation context)
    const message = messages.find(m => m.id === messageId);
    const envelope = (message as any)?.envelope;
    // planning_brief is authoritative - comes from user action (choice of plan)
    // DO NOT normalize, change, or regex when planning_brief is built
    // Include full proposal content including stats when user selects option 2/3
    const brief = {
      type: 'option_select',
      from_plan_id: planId,
      option_label: label,
      prior_prompt: lastUser,
      // Include orchestrator intent_summary from envelope (multi-turn conversation context)
      ...(envelope?.intent_summary ? { intent_summary: envelope.intent_summary } : {}),
      ...(envelope?.intent_guess ? { intent_guess: envelope.intent_guess } : {}),
      proposal: {
        title: chosenProposal?.title,
        chart_family: chosenProposal?.chart_family,
        brief: chosenProposal?.brief,
        mappings_brief: chosenProposal?.mappings_brief || {},
        stats: chosenProposal?.stats || [], // Include stats tokens for option 2/3
      },
    } as any;

    const res = await runWithGuards(async () => {
      resetRunState('approve');
      setIsProcessing(true);
      setStageRecipe();
      const ok = await materializeAndRunFromBrief(brief);
      return ok;
    });

    if (!res.ok) {
      setMessages(prev => prev.map(msg => msg.id === messageId ? { ...msg, approved: false } : msg));
      if (!getDatasetRef()) {
        toast({ title: 'Dataset required', description: 'Pick or upload a dataset to enable Approve & run.' });
        try { trackClient('approve_run_failed', { reason: 'dataset', planId }); } catch {}
        markTerminal();
      } else if (insufficient) {
        toast({ title: 'Insufficient credits', description: 'Please purchase more credits to continue.' });
        try { trackClient('approve_run_failed', { reason: 'credits', planId }); } catch {}
        setBillingOpen(true);
        markTerminal();
        return;
      } else {
        if (res.reason === 'error') {
          showRunErrorToast((res as any).error);
        } else {
          toast({ title: 'Run didn\'t complete', description: 'Open Logs for details, then try again in a moment.' });
        }
        try { trackClient('approve_run_failed', { reason: 'api', planId }); } catch {}
        markTerminal();
      }
    } else if (res.value === true) {
      setMessages(prev => prev.map(msg => msg.id === messageId ? { ...msg, approved: true, requiresApproval: false, selectedCard: { label, title: String(chosenProposal?.title || ''), recommended: false, prop: chosenProposal, plan: planFromProposal(basePlan, chosenProposal) } } : msg));
      try { trackClient('approve_run_succeeded', { planId }); } catch {}
    } else {
      toast({ title: 'Run didn\'t complete', description: 'Open Logs for details, then try again in a moment.' });
      try { trackClient('approve_run_failed', { reason: 'api', planId }); } catch {}
      markTerminal();
    }
  };

  // Handle file upload completion
  const handleFileUploaded = async (fileData: any) => {
    console.log('📤 File uploaded, processing...', fileData);
    try { if (fileData?.id) setSelectedFileId(String(fileData.id)); } catch {}
    // Ensure dataset list in chat refreshes immediately after upload
    try { queryClient.invalidateQueries({ queryKey: ['files'] }); } catch {}
    // Pre-clear live fingerprint mapping only if switching dataset will occur downstream
    try {
      const datasetRef = { dataset_id: undefined as any, dataset_path: undefined as any, filename: String(fileData?.filename || fileData?.originalName || ''), size: Number(fileData?.size || 0) } as { dataset_id?: string; dataset_path?: string; filename?: string; size?: number };
      if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[dataSelected]', { datasetRef, ts: Date.now(), source: 'upload' });
      window.dispatchEvent(new CustomEvent('lumi:dataSelected', { detail: { datasetRef, ts: Date.now() } }));
      try {
        const draftId = String(routeDraftId || (currentPlot as any)?.id || '');
        const dsHash = quickHashHex(String(fileData?.fingerprintId || dataFingerprintId || ''));
        const cols = (() => { try { const f = (window as any).__last_fp_for_chat; return Array.isArray(f?.columns) ? f.columns.length : undefined; } catch { return undefined; } })();
        track('dataset:ready' as any, { draft_id: draftId, dataset_id_hash: dsHash, cols, bind_method: 'upload' } as any);
      } catch {}
    } catch {}
    
    // Check if fingerprint was already created during upload (canonical path)
    if (fileData.fingerprintId && fileData.fingerprint) {
      try { ensureSummaryPlaceholder(String(fileData.fingerprintId), fileData.fingerprint); } catch {}
      // Reset builder state when switching to new dataset
      // This ensures builder doesn't carry over state from previous dataset
      setPickedChartId(null);
      setBuilderBindings({});
      lastBuilderTextRef.current = '';
      const newFpId = String(fileData.fingerprintId);
      userTouchedInputRef.current.delete(newFpId); // Reset touched flag for new dataset
      setDataFingerprintId(fileData.fingerprintId);
      try {
        const pid = String(currentProject?.id || '');
        const cid = String(routeDraftId || (currentPlot as any)?.id || '');
        if (pid && cid) setLiveDataFingerprint(pid, cid, String(fileData.fingerprintId), { fileId: fileData.id });
      } catch {}

      // Cache for Data tab and preview
      // Avoid window globals for fingerprint; keep only table cache for Data Preview fallback
      if (typeof window !== 'undefined' && Array.isArray(fileData.data)) {
        (window as any).lastDataTable = fileData.data;
      }

      // Always fetch the canonical fingerprint by fileId to avoid stale or partial values
      try { await fetchFingerprint(fileData.id); } catch {}
      return;
    }
    
    // Otherwise, analyze the data separately
    if (fileData.data && fileData.data.length > 0) {
      try {
        const response = await apiRequest('POST', '/api/ai/v2/analyze-upload', {
          data: fileData.data,
          fileId: fileData.id,
          projectId: currentProject?.id
        });

        const result = await response.json();
          if (result.success) {
          try { ensureSummaryPlaceholder(String(result.fingerprintId), result.fingerprint); } catch {}
          // Reset builder state when switching to new dataset
          setPickedChartId(null);
          setBuilderBindings({});
          lastBuilderTextRef.current = '';
          const newFpId = String(result.fingerprintId);
          userTouchedInputRef.current.delete(newFpId); // Reset touched flag for new dataset
          setDataFingerprintId(result.fingerprintId);
          try {
            const pid2 = String(currentProject?.id || '');
            const cid2 = String(routeDraftId || (currentPlot as any)?.id || '');
            if (pid2 && cid2) setLiveDataFingerprint(pid2, cid2, String(result.fingerprintId), { fileId: fileData.id });
          } catch {}
          
          // Cache for Data Preview fallback and summary card rendering (dev/demo only)
          try {
            if (typeof window !== 'undefined') {
              (window as any).lastDataAnalysis = result.analysis;
              (window as any).lastDataTable = fileData.data;
              (window as any).__last_fp_for_chat = {
                rows: result.analysis.rows,
                columns: result.analysis.columns,
                suggestions: { plotTypes: result.analysis.suggestedPlots || [] },
                patterns: result.analysis.patterns || {}
              };
            }
          } catch {}
          
          // Append intro BEFORE the summary card placeholder
          try {
            const intro: ChatMessage = { id: Date.now().toString() + '_' + Math.random().toString(36).slice(2,6) + '_fp_intro', role: 'assistant', content: "Here is a quick summary of your data:", timestamp: new Date(), asMarkdown: true } as any;
            const cardId = Date.now().toString() + '_' + Math.random().toString(36).slice(2,6) + '_fp_card';
            const cardWrapper: ChatMessage = { id: cardId, role: 'assistant', content: '', timestamp: new Date(), asMarkdown: false } as any;
            setMessages(prev => [...prev, intro, cardWrapper]);
          } catch {}
        }
      } catch (error) {
        console.error('❌ Error analyzing data:', error);
      }
    }
  };

  // Expose handleFileUploaded to window for TourManager
  useEffect(() => {
    try { (window as any).__lumiHandleFileUploaded = handleFileUploaded; } catch {}
    return () => {
      try { delete (window as any).__lumiHandleFileUploaded; } catch {}
    };
  }, [handleFileUploaded]);

  // Watch for sample data uploading state from TourManager
  useEffect(() => {
    const checkSampleDataUploading = () => {
      const isUploading = !!(window as any).__lumiIsSampleDataUploading;
      setIsSampleDataUploading(isUploading);
    };

    // Check initially
    checkSampleDataUploading();

    // Poll for changes (since window variable changes don't trigger React re-renders)
    const interval = setInterval(checkSampleDataUploading, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-full bg-card border-r border-border flex flex-col" data-testid="lumi-chat-v2">
      {/* Chat Header (fixed height for alignment) */}
      <div className="h-12 px-4 border-b border-border flex items-center">
        {/* Gallery and Multi-panel buttons */}
        <div className="flex gap-2 w-full">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const pid = currentProject?.id || '550e8400-e29b-41d4-a716-446655440000';
              try {
                setLocation(`/project/${String(pid)}/gallery`);
              } catch {}
            }}
            className="flex-1 h-9 text-xs"
            title="View Gallery"
          >
            <Images className="h-3 w-3 mr-1" />
            Gallery
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const pid = currentProject?.id;
              if (!pid) return;
              try {
                setLocation(`/project/${String(pid)}/figures`);
              } catch {}
            }}
            className="flex-1 h-9 text-xs"
            title="Multi-panel Figures"
          >
            <Grid3x3 className="h-3 w-3 mr-1" />
            Multi-panel
          </Button>
        </div>
      </div>
      
      {/* Stage indicator: minimal, with animated dots, embedded under header */}
      {null}

      {/* Chat Messages */}
      <ScrollArea className="flex-1 p-1" ref={scrollRef} data-testid="chat-messages"
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!isDropUploading) e.currentTarget.classList.add('ring-2','ring-primary/40'); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('ring-2','ring-primary/40'); }}
        onDrop={async (e) => {
          e.preventDefault(); e.stopPropagation();
          e.currentTarget.classList.remove('ring-2','ring-primary/40');
          if (isDropUploading) { toast({ title: 'Uploading…', description: 'Hang tight, this usually takes a few seconds.' }); return; }
          if (!currentProject?.id) { toast({ title: 'Project required', description: 'Choose a project, then use Upload to add your file.' }); return; }
          const dt = e.dataTransfer;
          if (!dt || !dt.files || dt.files.length === 0) return;
          const file = dt.files[0];
          try {
            setIsDropUploading(true);
            setShowDataModal(false);
            const form = new FormData();
            form.append('file', file);
            const resp = await authorizedFetch(`/api/projects/${currentProject.id}/upload`, { method: 'POST', body: form });
            if (!resp.ok) throw new Error('Upload failed');
            const uploaded = await resp.json();
            await handleFileUploaded(uploaded);
            toast({ title: 'Upload complete', description: file.name });
          } catch (err) {
            console.error('Drop upload failed', err);
            toast({ title: 'Upload failed', description: 'Please select a CSV (.csv) and try again.' });
          } finally {
            setTimeout(() => setIsDropUploading(false), 2000);
          }
        }}
      >
        <div className="min-h-0 space-y-1">
          {(dataFingerprintId || isDropUploading || isSampleDataUploading) && (
            <div className="mx-2 my-2">
              <span className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-full bg-green-500/10 text-green-700 border border-green-500/20">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
                {(isDropUploading || isSampleDataUploading) ? 'Uploading…' : 'Data ready'}
              </span>
            </div>
          )}
          {/* Messages list continues... */}

          {(() => {
            // Fix builder placement: insert builder for active dataset's fp card, not just first one
            // Check if message is fp_card for the current active dataset
            return messages.map((message) => {
              const isFpCard = !message.content && (window as any)?.__last_fp_for_chat && String(message.id).includes('_fp_card');
              // Only insert builder if this fp_card matches the active dataset's fingerprint
              const messageFpId = String(message.id).replace('_fp_card', '').trim();
              const isActiveDatasetFpCard = isFpCard && dataFingerprintId && messageFpId === String(dataFingerprintId);
              const insertHere = isActiveDatasetFpCard;
              return (
                <div key={message.id} className={`chat-message ${message.role === 'user' ? 'flex justify-end' : ''}`} data-testid={`message-${message.role}`}>
              {message.role === 'assistant' ? (
                <div className="p-3 text-sm leading-6 text-foreground">
                  {(() => {
                    const showAvatar = (message.forceAvatar || message.id === latestAssistantId) && !message.hideAvatar;
                    const showBadges = !!message.hasPlot;
                    if (!showAvatar && !showBadges) return null;
                    return (
                      <div className="flex items-center gap-2 mb-2">
                        {showAvatar && <LumiAvatarInteractive size={avatarSize} />}
                        {/* name removed per spec */}
                        {message.hasPlot && (
                          <span className="text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full">
                            Plot generated
                          </span>
                        )}
                        
                      </div>
                    );
                  })()}
                  {(() => {
                    // Render fingerprint card if special placeholder message
                    if (!message.content && (window as any)?.__last_fp_for_chat && String(message.id).includes('_fp_card')) {
                      // Attach a ref here so we can align this block to the top on first appearance
                      return (
                        <div className="mx-2 max-w-[720px] space-y-2" ref={summaryContainerRef}>
                          <FingerprintSummaryCard fp={(window as any).__last_fp_for_chat} />
                          {renderBuilder()}
                        </div>
                      );
                    }
                    if ((message as any).kind === 'insightsPanel' && (message as any).data) {
                      return <div className="mx-2 max-w-[720px]"><InsightsPanel data={(message as any).data} /></div>;
                    }
                    // Check if this is an insight message (has hideAvatar and asMarkdown, and content starts with "**Figure caption**")
                    const isInsightMessage = message.asMarkdown && message.hideAvatar && message.content?.includes('**Figure caption**');
                    if (message.asMarkdown) {
                      // Use streaming animation for insight messages with copy buttons
                      if (isInsightMessage) {
                        return <InsightMarkdownWithCopy text={message.content} />;
                      }
                      // Regular markdown rendering for other messages
                      return (
                        <div
                          className="text-foreground whitespace-pre-wrap leading-snug"
                          dangerouslySetInnerHTML={renderMarkdownLite(message.content)}
                        />
                      );
                    }
                    return <p className="text-foreground whitespace-pre-wrap leading-snug">{message.content}</p>;
                  })()}
                  
                  {/* Show plan details if present */}
                  {(message.requiresApproval && message.plan && !message.approved) ? (
                    <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="text-xs font-medium mb-2">Visualization plans</div>
                      {(() => {
                        const basePlan: any = message.plan; // PlanSpec.v1
                        const proposals: any[] = Array.isArray((basePlan as any)?.proposals) ? (basePlan as any).proposals : [];
                        const recommended = (basePlan as any)?.recommended || {};
                        // Build three cards from PlanSpec.v1: [recommended, proposal 2, proposal 3]
                        const cards = [
                          { label: 'Recommended', plan: basePlan, recommended: true, title: String(recommended?.title || 'Recommended'), rationale: String(recommended?.rationale || '') },
                          ...proposals.slice(1,3).map((p, i) => ({ label: `Option ${i+2}`, prop: p, plan: planFromProposal(basePlan, p), recommended: false, title: String(p?.title || `Option ${i+2}`) }))
                        ];
                        return (
                          <div className="grid grid-cols-1 gap-2" data-lumi="plan-cards-container">
                            {cards.map((c, i) => (
                              <div key={`${message.id}_card_${i}`} className={`p-2 rounded border ${c.recommended ? 'border-yellow-400 bg-yellow-50/30' : 'border-border bg-background/40'}`}>
                                {(() => {
                                  try {
                                    const draftId = String(message.planId || routeDraftId || (currentPlot as any)?.id || '') || '';
                                    const planIdStr = c.recommended ? 'recommended' : (i === 1 ? 'option_2' : 'option_3');
                                    const cf = c.recommended
                                      ? (recommended?.chart_family || basePlan?.chart_family || (basePlan as any)?.chart?.family || '-')
                                      : ((c as any)?.prop?.chart_family || '-');
                                    const key = `${draftId}:${planIdStr}`;
                                    if (draftId && !shownPlanCardsRef.current.has(key)) {
                                      shownPlanCardsRef.current.add(key);
                                      track('plan:card_shown' as any, {
                                        plan_id: planIdStr,
                                        draft_id: draftId,
                                        dataset_id_hash: quickHashHex(String(dataFingerprintId || '')),
                                        chart_type: String(cf || 'unknown'),
                                        rank: i + 1,
                                        recommended: !!c.recommended,
                                      } as any);
                                    }
                                  } catch {}
                                  return null;
                                })()}
                                {c.recommended ? (
                                  <>
                                    <div className="mb-0.5">
                                      <div className="text-xs font-medium">{c.label}</div>
                                    </div>
                                    {(() => {
                                      const cf = c.recommended
                                        ? (recommended?.chart_family || basePlan?.chart_family || basePlan?.chart?.family || '-')
                                        : (c as any)?.prop?.chart_family || '-';
                                      const x = c.recommended
                                        ? (recommended?.encodings?.x || basePlan?.encodings?.x || '-')
                                        : ((c as any)?.prop?.mappings_brief?.x || '-');
                                      const y = c.recommended
                                        ? (recommended?.encodings?.y || basePlan?.encodings?.y || '-')
                                        : ((c as any)?.prop?.mappings_brief?.y || '-');
                                      return (
                                        <div className="space-y-0.5 text-xs text-muted-foreground mb-1 leading-snug">
                                          <div className="text-foreground font-medium">{(c as any).title}</div>
                                          <div>Chart: {cf}</div>
                                          <div>X: {x}</div>
                                          <div>Y: {y}</div>
                                          {(() => {
                                            const statsList = ((c as any)?.plan?.recommended?.stats) || [];
                                            const tokens = Array.isArray(statsList) ? statsList.map((t: any) => String(t)) : [];
                                            if (!tokens.length) return (<div>Stats: NA</div>);
                                            const labels: any = {
                                              ttest_ind: 't-test (ind.)',
                                              ttest_rel: 't-test (paired)',
                                              welch: 'Welch t-test',
                                              anova: 'ANOVA',
                                              kruskal: 'Kruskal–Wallis',
                                              mannwhitney: 'Mann–Whitney U',
                                              wilcoxon: 'Wilcoxon',
                                              chi2: 'Chi-square',
                                              fisher: 'Fisher exact',
                                              ks_2samp: 'KS test',
                                              anderson: 'Anderson–Darling',
                                              rm_anova: 'RM-ANOVA',
                                              lmm: 'Linear mixed model',
                                              logistic: 'Logistic regression',
                                              poisson: 'Poisson regression',
                                              negbin: 'NegBin regression',
                                              zinb: 'ZINB regression',
                                              coxph: 'Cox PH',
                                              gam: 'GAM',
                                            };
                                            // Prefer an explicit test:<name> token
                                            let testName = '';
                                            for (const raw of tokens) {
                                              const m = String(raw).toLowerCase().match(/^test\s*:\s*([a-z0-9_\-]+)/i);
                                              if (m && m[1]) { testName = m[1]; break; }
                                            }
                                            if (!testName) {
                                              // Fallback: find first known test token
                                              const lower = tokens.map(t => t.toLowerCase());
                                              const knownOrder = ['anova','kruskal','ttest_ind','ttest_rel','welch','mannwhitney','wilcoxon','chi2','fisher','ks_2samp','anderson','rm_anova','lmm','logistic','poisson','negbin','zinb','coxph','gam'];
                                              const found = knownOrder.find(k => lower.includes(k) || lower.includes(`test:${k}`));
                                              if (found) testName = found;
                                            }
                                            if (!testName) return (<div>Stats: NA</div>);
                                            const label = labels[testName] || testName;
                                            return (<div>Stats: {label}</div>);
                                          })()}
                                          {c.recommended && (c as any).rationale && (
                                            <div className="mt-0.5 text-[11px] text-foreground leading-snug">{(c as any).rationale}</div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        disabled={isProcessing || execInFlightRef.current || !getDatasetRef()}
                                        onClick={() => {
                                          const isRecommended = !!c.recommended;
                                          try {
                                            // Emit run:confirmed on user click with client-generated run_id
                                            (async () => {
                                              const runId = (crypto as any)?.randomUUID?.() || (Math.random().toString(36).slice(2) + Date.now());
                                              const draftId = String(message.planId || routeDraftId || (currentPlot as any)?.id || '');
                                              const dsHash = await hashWeb(String(dataFingerprintId || ''));
                                              const colsSelected = (() => { try { const f = (window as any).__last_fp_for_chat; return Array.isArray(f?.columns) ? f.columns.length : 0; } catch { return 0; } })();
                                              const transformsHint = (() => { try { const rec = (c as any)?.plan?.recommended; return Array.isArray(rec?.transforms) ? rec.transforms.length : 0; } catch { return 0; } })();
                                              track('run:confirmed' as any, {
                                                run_id: runId,
                                                draft_id: draftId,
                                                dataset_id_hash: dsHash,
                                                model_name: 'python',
                                                columns_selected: colsSelected,
                                                transforms_hint: transformsHint,
                                                credits_spent_estimate: Number((import.meta as any).env?.VITE_CREDITS_PER_PLOT || 0),
                                              } as any);
                                              // Stash run_id for the upcoming execute request (best-effort)
                                              (window as any).__lumiPendingRunId = runId;
                                            })().catch(() => {});
                                          } catch {}
                                          if (isRecommended) {
                                            handleApprovePlan(message.id, c.plan, message.planId!);
                                          } else {
                                            const basePlan: any = message.plan;
                                            const proposals: any[] = Array.isArray(basePlan?.proposals) ? basePlan.proposals : [];
                                            const idx = i; // 0=Recommended, 1=Option 2, 2=Option 3
                                            const chosenProposal = idx === 1 ? proposals[1] : proposals[2];
                                            handleApproveOptionBrief(message.id, basePlan, message.planId!, chosenProposal, (idx === 1 ? 'Option 2' : 'Option 3'));
                                          }
                                        }}
                                        className={`flex-1 ${c.recommended ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'} text-xs py-1.5 px-3 rounded`}
                                        {...(c.recommended
                                          ? { 'data-lumi': 'btn-plan-recommended' }
                                          : {})}
                                      >
                                        Approve & run
                                      </Button>
                                      {!getDatasetRef() && (
                                        <div className="mt-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                          A dataset is required to run this plan. <button onClick={() => { setShowDataModal(true); setPickerMode('choose'); }} className="underline">Select dataset</button>
                                        </div>
                                      )}
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          setModifyCtx({
                                            messageId: message.id,
                                            basePlan: c.plan,
                                            baseLabel: (c.recommended ? 'Recommended' : (i === 1 ? 'Option 2' : 'Option 3')) as any,
                                            planId: message.planId!,
                                            selectedProposal: (c as any)?.prop,
                                          });
                                          setModifyDialogOpen(true);
                                          try {
                                            trackClient('plan_modify_opened', { planId: message.planId });
                                          } catch {}
                                        }}
                                        className="flex-1 bg-secondary text-secondary-foreground text-xs py-1.5 px-3 rounded"
                                      >
                                        Modify
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <Collapsible defaultOpen={false}>
                                    <div className="flex items-center justify-between mb-0.5">
                                      <div className="text-xs font-medium">{c.label}</div>
                                      <CollapsibleTrigger asChild>
                                        <Button variant="ghost" size="sm" aria-label={`Toggle ${c.label}`} className="h-7 px-2 text-xs [&[data-state=open]>svg]:rotate-180">
                                          <ChevronDown className="ml-1 h-3 w-3 opacity-70" />
                                        </Button>
                                      </CollapsibleTrigger>
                                    </div>
                                    <CollapsibleContent>
                                      {(() => {
                                        const cf = c.recommended
                                          ? (recommended?.chart_family || basePlan?.chart_family || basePlan?.chart?.family || '-')
                                          : (c as any)?.prop?.chart_family || '-';
                                        const x = c.recommended
                                          ? (recommended?.encodings?.x || basePlan?.encodings?.x || '-')
                                          : ((c as any)?.prop?.mappings_brief?.x || '-');
                                        const y = c.recommended
                                          ? (recommended?.encodings?.y || basePlan?.encodings?.y || '-')
                                          : ((c as any)?.prop?.mappings_brief?.y || '-');
                                        return (
                                          <div className="space-y-0.5 text-xs text-muted-foreground mb-1 leading-snug">
                                            <div className="text-foreground font-medium">{(c as any).title}</div>
                                            <div>Chart: {cf}</div>
                                            <div>X: {x}</div>
                                            <div>Y: {y}</div>
                                            {(() => {
                                              const statsList = ((c as any)?.plan?.recommended?.stats) || [];
                                              const tokens = Array.isArray(statsList) ? statsList.map((t: any) => String(t)) : [];
                                              if (!tokens.length) return (<div>Stats: NA</div>);
                                              const labels: any = {
                                                ttest_ind: 't-test (ind.)',
                                                ttest_rel: 't-test (paired)',
                                                welch: 'Welch t-test',
                                                anova: 'ANOVA',
                                                kruskal: 'Kruskal–Wallis',
                                                mannwhitney: 'Mann–Whitney U',
                                                wilcoxon: 'Wilcoxon',
                                                chi2: 'Chi-square',
                                                fisher: 'Fisher exact',
                                                ks_2samp: 'KS test',
                                                anderson: 'Anderson–Darling',
                                                rm_anova: 'RM-ANOVA',
                                                lmm: 'Linear mixed model',
                                                logistic: 'Logistic regression',
                                                poisson: 'Poisson regression',
                                                negbin: 'NegBin regression',
                                                zinb: 'ZINB regression',
                                                coxph: 'Cox PH',
                                                gam: 'GAM',
                                              };
                                              // Prefer an explicit test:<name> token
                                              let testName = '';
                                              for (const raw of tokens) {
                                                const m = String(raw).toLowerCase().match(/^test\s*:\s*([a-z0-9_\-]+)/i);
                                                if (m && m[1]) { testName = m[1]; break; }
                                              }
                                              if (!testName) {
                                                // Fallback: find first known test token
                                                const lower = tokens.map(t => t.toLowerCase());
                                                const knownOrder = ['anova','kruskal','ttest_ind','ttest_rel','welch','mannwhitney','wilcoxon','chi2','fisher','ks_2samp','anderson','rm_anova','lmm','logistic','poisson','negbin','zinb','coxph','gam'];
                                                const found = knownOrder.find(k => lower.includes(k) || lower.includes(`test:${k}`));
                                                if (found) testName = found;
                                              }
                                              if (!testName) return (<div>Stats: NA</div>);
                                              const label = labels[testName] || testName;
                                              return (<div>Stats: {label}</div>);
                                            })()}
                                          </div>
                                        );
                                      })()}
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          disabled={isProcessing || execInFlightRef.current || !getDatasetRef()}
                                          onClick={() => {
                                            const isRecommended = !!c.recommended;
                                            if (isRecommended) {
                                              handleApprovePlan(message.id, c.plan, message.planId!);
                                            } else {
                                              const basePlan: any = message.plan;
                                              const proposals: any[] = Array.isArray(basePlan?.proposals) ? basePlan.proposals : [];
                                              const idx = i; // 0=Recommended, 1=Option 2, 2=Option 3
                                              const chosenProposal = idx === 1 ? proposals[1] : proposals[2];
                                              handleApproveOptionBrief(message.id, basePlan, message.planId!, chosenProposal, (idx === 1 ? 'Option 2' : 'Option 3'));
                                            }
                                          }}
                                          className={`flex-1 ${c.recommended ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'} text-xs py-1.5 px-3 rounded`}
                                          {...(c.recommended
                                            ? { 'data-lumi': 'btn-plan-recommended' }
                                            : {})}
                                        >
                                          Approve & run
                                        </Button>
                                        {!getDatasetRef() && (
                                          <div className="mt-2 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                            A dataset is required to run this plan. <button onClick={() => { setShowDataModal(true); setPickerMode('choose'); }} className="underline">Select dataset</button>
                                          </div>
                                        )}
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            setModifyCtx({
                                              messageId: message.id,
                                              basePlan: c.plan,
                                              baseLabel: (c.recommended ? 'Recommended' : (i === 1 ? 'Option 2' : 'Option 3')) as any,
                                              planId: message.planId!,
                                              selectedProposal: (c as any)?.prop,
                                            });
                                            setModifyDialogOpen(true);
                                            try {
                                              trackClient('plan_modify_opened', { planId: message.planId });
                                            } catch {}
                                          }}
                                          className="flex-1 bg-secondary text-secondary-foreground text-xs py-1.5 px-3 rounded"
                                        >
                                          Modify
                                        </Button>
                                      </div>
                                    </CollapsibleContent>
                                  </Collapsible>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      {/* credits note removed */}
                    </div>
                  ) : (message.selectedCard ? (
                    <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="text-xs font-medium mb-1">Selected plan</div>
                      {(() => {
                        const sel = message.selectedCard as any;
                        const isRec = !!sel?.recommended;
                        const sanitize = (t: any) => String(t ?? '-').replace(/[\u200B-\u200D\uFEFF]/g, '');
                        const cfRaw = isRec
                          ? (sel?.plan?.recommended?.chart_family || sel?.plan?.chart_family || sel?.plan?.chart?.family || '-')
                          : (sel?.prop?.chart_family || '-');
                        const xRaw = isRec
                          ? (sel?.plan?.recommended?.encodings?.x || sel?.plan?.encodings?.x || '-')
                          : (sel?.prop?.mappings_brief?.x || '-');
                        const yRaw = isRec
                          ? (sel?.plan?.recommended?.encodings?.y || sel?.plan?.encodings?.y || '-')
                          : (sel?.prop?.mappings_brief?.y || '-');
                        const cf = sanitize(cfRaw);
                        const x = sanitize(xRaw);
                        const y = sanitize(yRaw);
                        return (
                          <div className="space-y-0.5 text-xs text-muted-foreground mb-1 leading-snug">
                            <div className="text-foreground font-medium">{sanitize(sel?.title || sel?.label)}</div>
                            <div>Chart: {cf}</div>
                            <div>X: {x}</div>
                            <div>Y: {y}</div>
                            {(() => {
                              const statsList = isRec
                                ? (sel?.plan?.recommended?.stats || [])
                                : (((sel as any)?.plan?.recommended?.stats) || ((sel as any)?.prop?.stats) || []);
                              const tokens = Array.isArray(statsList) ? statsList.map((t: any) => String(t)) : [];
                              if (!tokens.length) return (<div>Stats: NA</div>);
                              const labels: any = {
                                ttest_ind: 't-test (ind.)',
                                ttest_rel: 't-test (paired)',
                                welch: 'Welch t-test',
                                anova: 'ANOVA',
                                kruskal: 'Kruskal–Wallis',
                                mannwhitney: 'Mann–Whitney U',
                                wilcoxon: 'Wilcoxon',
                                chi2: 'Chi-square',
                                fisher: 'Fisher exact',
                                ks_2samp: 'KS test',
                                anderson: 'Anderson–Darling',
                                rm_anova: 'RM-ANOVA',
                                lmm: 'Linear mixed model',
                                logistic: 'Logistic regression',
                                poisson: 'Poisson regression',
                                negbin: 'NegBin regression',
                                zinb: 'ZINB regression',
                                coxph: 'Cox PH',
                                gam: 'GAM',
                              };
                              // Prefer an explicit test:<name> token
                              let testName = '';
                              for (const raw of tokens) {
                                const m = String(raw).toLowerCase().match(/^test\s*:\s*([a-z0-9_\-]+)/i);
                                if (m && m[1]) { testName = m[1]; break; }
                              }
                              if (!testName) {
                                // Fallback: find first known test token
                                const lower = tokens.map(t => t.toLowerCase());
                                const knownOrder = ['anova','kruskal','ttest_ind','ttest_rel','welch','mannwhitney','wilcoxon','chi2','fisher','ks_2samp','anderson','rm_anova','lmm','logistic','poisson','negbin','zinb','coxph','gam'];
                                const found = knownOrder.find(k => lower.includes(k) || lower.includes(`test:${k}`));
                                if (found) testName = found;
                              }
                              if (!testName) return (<div>Stats: NA</div>);
                              const label = labels[testName] || testName;
                              return (<div>Stats: {label}</div>);
                            })()}
                          </div>
                        );
                      })()}
                    </div>
                  ) : null)}
                  
                  {/* Show if plan was approved */}
                  {null}
                </div>
              ) : (
                <div className="bg-gray-50 text-foreground rounded-lg p-3 text-sm max-w-[80%]">
                  {message.content}
                </div>
              )}
                  {/* Builder is rendered together with the Data Summary card above */}
                </div>
              );
            });
          })()}
          {loadingStage && (() => {
            return (
              <div className="px-3 py-2">
                <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-2">
                  {(() => {
                    // Use stored progressLabel if available, otherwise fallback to stage-based label
                    const label = progressLabel || (loadingStage === 'recipe' ? 'Creating recipes' : loadingStage === 'cooking' ? 'Cooking' : 'Tasting');
                    const msg = String(progressMessage || '').trim();
                    const showMsg = msg && msg.toLowerCase() !== label.toLowerCase();
                    return (
                      <>
                        <span 
                          aria-live="polite"
                          className="inline-block"
                          style={{
                            backgroundImage: 'linear-gradient(90deg, #52525b 0%, #52525b 20%, #a1a1aa 50%, #52525b 80%, #52525b 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 3.5s ease-in-out infinite',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            color: 'transparent'
                          }}
                        >
                          {label}{'.'.repeat(dotTick)}
                        </span>
                        {showMsg && <span className="opacity-70"> - {msg}</span>}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })()}
          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </div>
        {/* Chart Builder Surface positioned after the Data summary card/messages */}
        {dataFingerprintId && !messages.some((m) => !m.content && String(m.id).includes('_fp_card')) && (
          <div className="mx-2 my-2 max-w-[720px]">
            {renderBuilder()}
          </div>
        )}
      </ScrollArea>
      
      {/* Banner above composer when insufficient - only show AFTER run completes and results are displayed */}
      {shouldShowGate && (
        <CreditGateBanner
          cost={COST}
          resetAt={credits?.resetAt}
          cancelAtPeriodEnd={!!credits?.cancelAtPeriodEnd}
          onManage={() => setBillingOpen(true)}
          place="chat"
        />
      )}

      {/* Chat Input */}
      <div
        className="p-3 border-t border-gray-200"
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={async (e) => {
          e.preventDefault(); e.stopPropagation();
          if (isDropUploading) { toast({ title: 'Uploading…', description: 'Hang tight, this usually takes a few seconds.' }); return; }
          if (!currentProject?.id) { toast({ title: 'Project required', description: 'Choose a project, then use Upload to add your file.' }); return; }
          const dt = e.dataTransfer;
          if (!dt || !dt.files || dt.files.length === 0) return;
          const file = dt.files[0];
          // UI is CSV-only for now
          try { if (!/\.csv$/i.test(file?.name || '')) { toast({ title: 'CSV only', description: 'Please upload a .csv file.' }); return; } } catch {}
          try {
            setIsDropUploading(true);
            setShowDataModal(false);
            const form = new FormData();
            form.append('file', file);
            const resp = await authorizedFetch(`/api/projects/${currentProject.id}/upload`, { method: 'POST', body: form });
            const uploaded = await resp.json();
            await handleFileUploaded(uploaded);
            toast({ title: 'Upload complete', description: file.name });
          } catch (err) {
            console.error('Drop upload failed', err);
            toast({ title: 'Upload failed', description: 'Please select a CSV (.csv) and try again.' });
          } finally {
            setTimeout(() => setIsDropUploading(false), 2000);
          }
        }}
      >
        <form onSubmit={handleSendMessage}>
          <div className="relative bg-white border border-gray-300 rounded-xl focus-within:border-gray-400 transition-colors" style={{ minHeight: '56px' }}>
            <div className="grid grid-cols-[auto_1fr_auto] items-end">
              {/* Textarea that expands upward */}
              <div className="col-span-3 px-3 pt-3 pb-1">
                <textarea
                  ref={textareaRef}
                  placeholder={askUserQuestion || (dataFingerprintId ? "What would you like to visualize?" : "Upload data and ask Lumi to create a visualization")}
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    // Mark input as user-touched when user manually edits it
                    // This prevents builder from auto-reinjecting prompt after user clears it
                    const currentFpId = dataFingerprintId || '';
                    if (currentFpId) {
                      // Mark as touched if user clears input or types custom text
                      const newValue = e.target.value;
                      const wasBuilderText = newValue === lastBuilderTextRef.current;
                      if (!wasBuilderText || newValue.trim() === '') {
                        userTouchedInputRef.current.set(currentFpId, true);
                      }
                    }
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={async (e) => {
                    e.preventDefault(); e.stopPropagation();
                    if (isDropUploading) { toast({ title: 'Uploading…', description: 'Hang tight, this usually takes a few seconds.' }); return; }
                    if (!currentProject?.id) { toast({ title: 'Project required', description: 'Choose a project, then use Upload to add your file.' }); return; }
                    const dt = e.dataTransfer;
                    if (!dt || !dt.files || dt.files.length === 0) return;
                    const file = dt.files[0];
                    try {
                      setIsDropUploading(true);
                      setShowDataModal(false);
                      const form = new FormData();
                      form.append('file', file);
                      const resp = await authorizedFetch(`/api/projects/${currentProject.id}/upload`, { method: 'POST', body: form });
                      const uploaded = await resp.json();
                      await handleFileUploaded(uploaded);
                      toast({ title: 'Upload complete', description: file.name });
                    } catch (err) {
                      console.error('Drop upload failed', err);
                      toast({ title: 'Upload failed', description: 'Please select a CSV (.csv) and try again.' });
                    } finally {
                      setTimeout(() => setIsDropUploading(false), 2000);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e as any);
                    }
                  }}
                  disabled={(insufficient && !isProcessing) || (isProcessing && !askUserQuestion)}
                  className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400 resize-none overflow-hidden"
                  style={{ 
                    minHeight: '24px',
                    maxHeight: '200px',
                    lineHeight: '1.5'
                  }}
                  data-testid="input-chat"
                  data-lumi="chat-input"
                />
              </div>
              
              {/* Bottom row with buttons */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setShowDataModal(true)}
                      data-lumi="btn-upload"
                      className="ml-2 mb-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      data-testid="button-upload-from-composer"
                    >
                      <Plus className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>{dataFingerprintId ? "Replace data" : "Upload data"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <div className="flex-1" /> {/* Spacer */}
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="submit"
                      className={`mr-2 mb-2 p-2 rounded-full transition-colors ${insufficient && !isProcessing ? 'bg-gray-200 text-gray-400 hover:bg-gray-200 cursor-not-allowed' : 'bg-[#FFD700] hover:bg-[#FFC700] text-gray-600'} ${isProcessing && !askUserQuestion ? 'opacity-80' : ''}`}
                      data-testid="button-send-message"
                      data-lumi="chat-send"
                      disabled={insufficient || (isProcessing && !askUserQuestion) || !inputValue.trim()}
                      aria-busy={isProcessing || showSendSpinner}
                      aria-label={isProcessing || showSendSpinner ? 'Sending…' : 'Send'}
                    >
                      {isProcessing || showSendSpinner ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" data-lumi="render-progress" />
                      ) : (
                        <ArrowUpIcon className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </TooltipTrigger>
                  {(insufficient && !isProcessing) ? (
                    <TooltipContent>
                      <p>Need {COST} credits. Resets {formatRelativeTime(credits?.resetAt)}</p>
                    </TooltipContent>
                  ) : null}
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </form>
      </div>

      {/* Add Data Modal: choose existing or upload new */}
      {showDataModal && (
        <Dialog open onOpenChange={() => setShowDataModal(false)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add data</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={noDatasets ? undefined : "outline"}
                  className={noDatasets ? 'bg-lumiplot-gold text-black hover:brightness-95' : undefined}
                  onClick={() => { setShowDataModal(false); setShowUpload(true); }}
                  data-testid="button-upload-new"
                  data-lumi="btn-upload"
                >
                  Upload new
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPickerMode('choose')}
                  data-testid="button-choose-existing"
                >
                  Choose existing
                </Button>
              </div>

              {pickerMode === 'choose' ? (
                <div className="space-y-3">
                  <Select value={selectedFileId ?? undefined} onValueChange={(v) => setSelectedFileId(v)} disabled={!pid}>
                    <SelectTrigger>
                      <SelectValue placeholder={pid ? (files.length === 0 ? 'No datasets found' : 'Select a dataset') : 'Select a project'} />
                    </SelectTrigger>
                    <SelectContent>
                      {(files || []).map((f: any) => {
                        const base = String((((f as any).metadata as any)?.originalName || (f as any).filename || (f as any).objectPath || (f as any).id));
                        let hint = '';
                        try {
                          const m = (f.metadata || {}) as any;
                          const rows = m.rowCount ?? m.rows ?? m.fingerprint?.rows;
                          const cols = m.columnCount ?? (Array.isArray(m.fingerprint?.columns) ? m.fingerprint.columns.length : undefined);
                          hint = rows ? ` · ${rows} rows${cols ? ` × ${cols} cols` : ''}` : (cols ? ` · ${cols} cols` : '');
                        } catch {}
                        const label = `${base}${hint}`;
                        return (
                          <SelectItem key={f.id} value={String(f.id)} textValue={label}>
                            {label}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <div className="flex justify-end">
                    <Button
                      disabled={!selectedFileId || isChoosingDataset}
                      className={selectedFileId ? 'bg-lumiplot-gold text-black hover:brightness-95' : undefined}
                      onClick={async () => {
                        if (isChoosingDataset) return;
                        setIsChoosingDataset(true);
                        const f = (files || []).find((x: any) => String(x.id) === selectedFileId);
                        if (!f) { setIsChoosingDataset(false); return; }
                        try {
                          // Pre-clear and early selection event before fetching fingerprint
                          try {
                            const pid3 = String(currentProject?.id || '');
                            const cid3 = String(routeDraftId || (currentPlot as any)?.id || '');
                            if (pid3 && cid3) setLiveDataFingerprint(pid3, cid3, null as any);
                          } catch {}
                          try {
                            const datasetRef = { dataset_id: undefined as any, dataset_path: undefined as any, filename: (((f as any).metadata as any)?.originalName) || (f as any).filename || (f as any).objectPath || (f as any).id, size: Number(((f as any).metadata as any)?.size || (f as any).size || 0) } as { dataset_id?: string; dataset_path?: string; filename?: string; size?: number };
                            if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[dataSelected]', { datasetRef, ts: Date.now(), source: 'choose-existing' });
                            window.dispatchEvent(new CustomEvent('lumi:dataSelected', { detail: { datasetRef, ts: Date.now() } }));
                            // emit after fingerprint fetch below
                          } catch {}
                        const resp = await apiRequest('GET', `/api/ai/v2/fingerprint/${f.id}`);
                          const j = await resp.json();
                          if (j?.success) {
                            // Reset builder state when switching to new dataset
                            setPickedChartId(null);
                            setBuilderBindings({});
                            lastBuilderTextRef.current = '';
                            const newFpId = String(j.fingerprintId);
                            userTouchedInputRef.current.delete(newFpId); // Reset touched flag for new dataset
                            setDataFingerprintId(j.fingerprintId);
                          try {
                            const pid3 = String(currentProject?.id || '');
                            const cid3 = String(routeDraftId || (currentPlot as any)?.id || '');
                            if (pid3 && cid3) setLiveDataFingerprint(pid3, cid3, String(j.fingerprintId), { fileId: f.id });
                          } catch {}
                            try {
                              const draftId = String(routeDraftId || (currentPlot as any)?.id || '');
                              const dsHash = quickHashHex(String(j?.fingerprintId || dataFingerprintId || ''));
                              const cols = (() => { try { const fpf = j?.fingerprint; return Array.isArray(fpf?.columns) ? fpf.columns.length : undefined; } catch { return undefined; } })();
                              track('dataset:ready' as any, { draft_id: draftId, dataset_id_hash: dsHash, cols, bind_method: 'select' } as any);
                            } catch {}
                            // After successful fingerprint fetch, ensure summary placeholder and open builder
                            try { ensureSummaryPlaceholder(String(j.fingerprintId), j.fingerprint || {}); } catch {}
                            try {
                              setShowBuilder(true);
                              setBuilderOpen(true);
                              if ((import.meta as any).env?.VITE_NAV_DEBUG) console.log('[builder]', { open: true, reason: 'after-fingerprint-success' });
                            } catch {}
                            try {
                              const tableRes = await apiRequest('GET', `/api/files/${f.id}/data`);
                              if (tableRes.ok) {
                                const table = await tableRes.json();
                                if (Array.isArray(table) && table.length > 0) (window as any).lastDataTable = table;
                              }
                            } catch {}
                            try {
                              window.dispatchEvent(new CustomEvent('lumi:dataAnalyzed', {
                                detail: {
                                  filename: (((f as any).metadata as any)?.originalName) || (f as any).filename || (f as any).objectPath || (f as any).id,
                                  rows: j.rows,
                                  columns: j.columns,
                                  fingerprintId: j.fingerprintId,
                                  fileId: f.id,
                                  fingerprint: j.fingerprint
                                }
                              }));
                            } catch {}
                          }
                          setShowDataModal(false);
                        } catch {
                          // keep modal open so user can retry
                        } finally {
                          setIsChoosingDataset(false);
                        }
                      }}
                      data-testid="button-use-selected"
                    >
                      {isChoosingDataset ? 'Loading…' : 'Use selected'}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* File Upload Modal */}
      {showUpload && currentProject && (
        <FileUpload
          projectId={currentProject.id}
          onClose={() => setShowUpload(false)}
          onFileUploaded={handleFileUploaded}
        />
      )}
      {/* Dirty State Confirmation Dialog */}
      <Dialog open={showDirtyConfirm} onOpenChange={setShowDirtyConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved changes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>You have unsaved changes to the current plot. Choose how to proceed.</p>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="secondary" onClick={() => { setShowDirtyConfirm(false); pendingSendRef.current = null; }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={async () => {
              setShowDirtyConfirm(false);
              // Discard changes and start new plot
              const fn = pendingSendRef.current;
              pendingSendRef.current = null;
              if (fn) await fn();
            }}>
              Discard and start new plot
            </Button>
            <Button onClick={async () => {
              setShowDirtyConfirm(false);
              // Save current draft then start new plot
              if (onSave) {
                try {
                  await onSave();
                } catch (err) {
                  toast({ title: 'Save failed', description: 'Could not save current draft. Please try again.', variant: 'destructive' });
                  return;
                }
              }
              const fn = pendingSendRef.current;
              pendingSendRef.current = null;
              if (fn) await fn();
            }}>
              Save then start new plot
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {modifyDialogOpen && modifyCtx && (
        <ModifyPlanDialog
          plan={modifyCtx.basePlan}
          open={modifyDialogOpen}
          onClose={() => setModifyDialogOpen(false)}
          onSubmit={async (rawText) => {
            try {
              const lastUser = [...messages].reverse().find((m) => m.role === 'user')?.content || '';
              const text = String(rawText || '').trim();
              const parsed = (() => {
                const s = text.toLowerCase();
                const out: any = {};
                const bins = s.match(/bins?\s*(\d{1,3})/i);
                if (bins && bins[1]) out.bins = Math.max(1, Math.min(200, parseInt(bins[1], 10)));
                if (/histogram/.test(s)) out.chart = 'histogram';
                if (/violin/.test(s)) out.chart = 'violin';
                if (/box\s*plot|boxplot/.test(s)) out.chart = 'box';
                if (/log\s*x/.test(s)) out.log_x = true;
                if (/log\s*y/.test(s)) out.log_y = true;
                if (/swap\s*axes|swap\s*x.*y|swap\s*y.*x/.test(s)) out.swap_axes = true;
                const facet = s.match(/facet\s*by\s*([a-zA-Z0-9_\- ,]+)/);
                if (facet && facet[1]) out.facet_by = facet[1].split(/[, ]+/).filter(Boolean);
                return out;
              })();

              const isRec = modifyCtx.baseLabel === 'Recommended';
              const rec = (modifyCtx.basePlan?.recommended || {}) as any;
              const selectedProposal = isRec
                ? {
                    title: rec?.title,
                    chart_family: rec?.chart_family,
                    brief: rec?.rationale,
                    mappings_brief: {
                      x: rec?.encodings?.x ?? null,
                      y: rec?.encodings?.y ?? null,
                      color: rec?.encodings?.color ?? null,
                    },
                  }
                : {
                    title: modifyCtx.selectedProposal?.title,
                    chart_family: modifyCtx.selectedProposal?.chart_family,
                    brief: modifyCtx.selectedProposal?.brief,
                    mappings_brief: modifyCtx.selectedProposal?.mappings_brief || {},
                  };

              const brief = {
                mode: 'user_modify',
                from_plan_id: modifyCtx.planId,
                base_option_label: modifyCtx.baseLabel,
                prior_prompt: lastUser,
                selected_proposal: selectedProposal,
                user_modification: { raw_text: text, parsed },
                base_plan_hint: {
                  chart_family: rec?.chart_family || modifyCtx.basePlan?.chart_family,
                  encodings: rec?.encodings || modifyCtx.basePlan?.encodings,
                },
                session: { projectId: currentProject?.id || null, userId: user?.id || null },
              } as any;

              trackClient('plan_modify_submitted', { planId: modifyCtx.planId, chars: text.length });

              // Immediate stage: planning starts
              setStageRecipe();

              // Show Selected plan confirmation card immediately
              try {
                setMessages(prev => prev.map(msg => {
                  if (msg.id !== modifyCtx.messageId) return msg;
                  const isRecLocal = modifyCtx.baseLabel === 'Recommended';
                  const card = isRecLocal
                    ? { label: 'Recommended', title: String(selectedProposal?.title || 'Recommended'), recommended: true, plan: modifyCtx.basePlan }
                    : { label: (modifyCtx.baseLabel as any), title: String(selectedProposal?.title || String(modifyCtx.baseLabel)), recommended: false, prop: modifyCtx.selectedProposal };
                  return { ...msg, approved: true, requiresApproval: false, selectedCard: card as any };
                }));
              } catch {}

              const ok = await materializeAndRunFromBrief(brief);
              if (!ok) {
                trackClient('plan_modify_failed', { planId: modifyCtx.planId });
                toast({ title: 'Could not update plan', description: 'Please adjust your instructions and try again.' });
                return;
              }
              setModifyDialogOpen(false);
              setModifyCtx(null);
              // Scroll chat to bottom so the user sees status immediately
              try {
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="chat-messages"]');
                  if (el) el.scrollTop = (el as any).scrollHeight;
                  const end = document.querySelector('[data-testid="chat-messages"] div:last-child');
                  if (end && (end as any).scrollIntoView) (end as any).scrollIntoView({ behavior: 'smooth' });
                }, 0);
              } catch {}
            } catch (e) {
              trackClient('plan_modify_failed', { planId: modifyCtx.planId });
              toast({ title: 'Could not update plan', description: 'Please try again.' });
            }
          }}
        />
      )}
      {/* Billing modal controlled here so any gate can open it */}
      <BillingModal open={billingOpen} onOpenChange={(o) => setBillingOpen(o)} />
    </div>
  );
}

